import { AgGridModule } from '@ag-grid-community/angular';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import type {
  CellClassParams,
  CellClickedEvent,
  CellEditingStartedEvent,
  CellEditingStoppedEvent,
  CellValueChangedEvent,
  ColumnApi,
  ColumnResizedEvent,
  EditableCallbackParams,
  GetRowIdParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  RowClassParams,
  RowClickedEvent,
  RowNode,
} from '@ag-grid-community/core';
import { ModuleRegistry } from '@ag-grid-community/core';
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel';
import { LicenseManager } from '@ag-grid-enterprise/core';
import { MenuModule } from '@ag-grid-enterprise/menu';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';
import { SideBarModule } from '@ag-grid-enterprise/side-bar';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  type EventEmitter,
  Inject,
  Input,
  EventEmitter as NgEventEmitter,
  type OnChanges,
  type OnDestroy,
  type OnInit,
  Optional,
  Output,
  type SimpleChanges,
} from '@angular/core';
import type { CdwGridColumn, CdwGridSort } from '@cdw-ae/common-data';
import { FilterBarModule } from '@cdw-ae/filter-bar-core';
import type {
  FilterBarField,
  SelectedFilterField,
} from '@cdw-ae/filter-bar-interfaces';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { CDW_DEFAULT_SIDEBAR } from '../config/cdw-default-sidebar.constant';
import { CDW_SAVE_COMPONENT_STATE_SERVICE_TOKEN } from '../config/cdw-save-component-state-service-token.constant';
import { CustomHeaderComponent } from '../custom-header/custom-header.component';
import { GridFooterComponent } from '../grid-footer/grid-footer.component';
import type { CdwColDef } from '../interfaces/cdw-col-def.interface';
import type { CdwSidebarDef } from '../interfaces/cdw-sidebar-def.interface';
import type {
  ICdwSaveComponentStateService,
  ICdwSearchGridDataService,
} from '../interfaces/data-source.interface';
import { PreviewIconComponent } from '../preview-icon/preview-icon.component';
import {
  type PersistedGridState,
  readPersistedColumnState,
  readPersistedPageSize,
  resolveCacheBlockSize,
  toPersistedColumnState,
} from './column-state-persistence';
import {
  EditableCellWithUndoComponent,
  type EditableCellWithUndoGridHost,
} from './editable-cell-with-undo.component';
import { isFilterChangePermissive } from './filter-permissiveness';
import { mintOrRestoreSessionId } from './grid-session-id';
import { revertNodeSelectionToServerTruth } from './selection-lock-revert';
import { buildSqlSsrmDatasource } from './sql-ssrm-datasource.factory';
import { SsrmHeaderCheckboxComponent } from './ssrm-header-checkbox.component';
import { SsrmSelectAllBannerComponent } from './ssrm-select-all-banner.component';
import { SsrmSelectAllPromptService } from './ssrm-select-all-prompt.service';

// AG-Grid module + license registration. Module-load side-effect so any
// standalone consumer of `CdwSsrmGridComponent` automatically gets the
// required modules registered before the component instantiates.
//
// Why a top-level side-effect: AG-Grid's `ModuleRegistry` is a global state
// that must be populated BEFORE the first grid instantiation. The legacy
// `CdwSearchGridModule` did this from its NgModule constructor, which only
// fired when consumers imported that NgModule. Standalone components can't
// rely on that — when consumers import this component directly, only THIS
// file's module-load body runs. Deferring registration to component
// construction is too late (AG-Grid checks the registry at the start of
// `setServerSideDatasource`, which fires from `onGridReady`, which fires
// before our component's view-init).
//
// `ModuleRegistry.registerModules` is idempotent and `LicenseManager
// .setLicenseKey` is safe to call multiple times — so duplicate
// registration from CdwSearchGridModule (legacy consumers) is harmless.
/**
 * Sentinel value passed as the `source` argument to `node.setDataValue(...)`
 * when programmatically reverting a cell from `handleCellUndoClicked()`.
 * AG-Grid propagates this onto `CellValueChangedEvent.source`, where our
 * own `onCellValueChanged` checks it and short-circuits — without that
 * round-trip block, the undo would immediately re-add the field to
 * `modifiedCellsByRow` and re-emit `(cellValueChanged)` to the consumer,
 * defeating the undo entirely.
 *
 * The string is deliberately distinct from any of AG-Grid's built-in
 * source values (`'edit'`, `'paste'`, `'rangeService'`, etc.) so a future
 * AG-Grid upgrade can't collide.
 */
const CDW_UNDO_EVENT_SOURCE = 'cdw-undo';

LicenseManager.setLicenseKey(
  'Using_this_AG_Grid_Enterprise_key_( AG-041717 )_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_( legal@ag-grid.com )___For_help_with_changing_this_key_please_contact_( info@ag-grid.com )___( Amplified It LLC )_is_granted_a_( Multiple Applications )_Developer_License_for_( 7 )_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_AG_Grid_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_( 2 )_Production_Environments___This_key_works_with_AG_Grid_Enterprise_versions_released_before_( 9 February 2023 )____[v2]_MTY3NTkwMDgwMDAwMA==fde3befd306bf03b02dc772150bedb3c'
);
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ServerSideRowModelModule,
  ColumnsToolPanelModule,
  MenuModule,
  SideBarModule,
]);

/**
 * Server-Side Row Model variant of the search grid.
 *
 * Generic `T` constraint includes the universal flags `isStale` and
 * `isUpdating` that the augmentation layer and `PreviewIconComponent`
 * cell renderer rely on.
 *
 * SSRM data-fetch translation is delegated to `buildSqlSsrmDatasource(...)`
 * in `sql-ssrm-datasource.factory.ts` so the pure logic can be unit-tested
 * without bringing the Angular/AG-Grid runtime into the test environment.
 */
@Component({
  selector: 'lib-cdw-ssrm-grid',
  standalone: true,
  imports: [
    CommonModule,
    AgGridModule,
    FilterBarModule,
    SsrmSelectAllBannerComponent,
    GridFooterComponent,
  ],
  // Per-grid-instance prompt service: each <lib-cdw-ssrm-grid> on a page
  // gets its own SsrmSelectAllPromptService, allowing multiple grids to
  // have independent banner/prompt state. Injected by
  // SsrmHeaderCheckboxComponent (sets the prompt) and the banner
  // (subscribes to it).
  providers: [SsrmSelectAllPromptService],
  templateUrl: './cdw-ssrm-grid.component.html',
  styleUrls: ['./cdw-ssrm-grid.component.scss'],
  host: {
    // Toggled by the selectionLockActive$ subscription. CSS in the
    // component's stylesheet uses this class to gray out checkboxes
    // (.ag-checkbox-input-wrapper { opacity, pointer-events }) and
    // attach a tooltip via title attribute.
    '[class.cdw-ssrm-grid--selection-locked]': 'currentLockActive',
    '[attr.title]':
      'currentLockActive ? "Selection locked while job is running." : null',
  },
})
export class CdwSsrmGridComponent<
  T extends { id: string | number; isStale?: boolean; isUpdating?: boolean },
> implements OnChanges, OnDestroy, OnInit
{
  // === Grid contract ===
  /** AG-Grid column definitions, extended with CdwColDef helpers. */
  @Input() columnDefs: CdwColDef[] = [];
  /**
   * Consumer-supplied data service. The SSRM datasource invokes
   * `fetchSqlData(...)` once per requested block. Currently sql-only — the
   * `fetchApiData` path is reserved for a future api-source variant.
   */
  @Input() cdwSearchGridDataService!: ICdwSearchGridDataService<T>;
  /**
   * REQUIRED. Stable identifier for this grid view (e.g. `'device-search'`,
   * `'user-search'`). Used as the sessionStorage namespace key for the
   * grid's per-instance session UUID — two grids on the same page in the
   * same tab get distinct sessions iff they have distinct viewKeys. The
   * backend uses (customerId, fbUserId, viewKey, sessionUuid) to scope the
   * server-side selection set in Redis (EDUS-2484), so changing the value
   * mid-session would orphan any in-progress selection.
   *
   * No default — consumers MUST specify. Made required so a forgotten
   * binding fails loudly at runtime (sessionStorage key would collide
   * across grids) instead of silently sharing selection state across
   * unrelated grids.
   */
  @Input() viewKey!: string;
  /**
   * Identifier under which this grid's column layout — order, visibility,
   * and width — is persisted. Used as the key passed to the optional
   * `CDW_SAVE_COMPONENT_STATE_SERVICE_TOKEN` service (localStorage-backed in
   * spog-client via `ComponentStateService`). Distinct grids that should
   * remember independent layouts MUST pass distinct values. When the token
   * isn't provided, persistence is silently skipped (the grid still works).
   */
  @Input() componentId = 'cdw-ssrm-grid';
  /**
   * Singular noun for this grid's row entity (e.g. "device"). Used in the
   * synthesized "Failed to retrieve {itemLabel}s" error message when the
   * data service rejects with a non-Error value.
   */
  @Input() itemLabel = 'item';
  /** Initial page size for AG-Grid's pagination control. Default 25. */
  @Input() paginationPageSize = 25;
  /** SSRM cache block size — number of rows fetched per `getRows` call. */
  @Input() cacheBlockSize = 50;
  /**
   * When true, render the "Rows Per Page" selector in the footer (EDUS-2713).
   * Off by default so grids that don't want it (e.g. fixed-size lists) are
   * unaffected. The chosen size persists via the same `saveStateService`
   * blob as column layout, keyed by `componentId`.
   */
  @Input() showPageSizeSelector = false;
  /** Guards the `.ag-paging-panel`-not-found warning in `mountPageSizeSelectorInPaginator` to fire once per instance rather than on every `paginationChanged`. */
  private pageSizeSelectorMountWarned = false;
  /** Page-size options offered by the selector. */
  @Input() pageSizeOptions: number[] = [25, 50, 100, 200, 500];
  /**
   * Maximum SSRM blocks held in cache. Older blocks evict on overflow; the
   * UX contract for evicted-row state surfacing is documented in the epic
   * README under "Row-state surfacing".
   */
  @Input() maxBlocksInCache = 2;
  /**
   * Optional 0-indexed page to restore on first render — used by consumers
   * navigating back into the grid (e.g. from a device summary). Applied in
   * `onFirstDataRendered`.
   */
  @Input() initialPage?: number;
  /** HTML-rendered overlay shown when the datasource returns 0 rows. */
  @Input() noRowsOverlayText = 'No data matches the current filter settings.';

  // === Select-all-matching banner ===
  /**
   * When true (default), renders the Gmail-style select-all-matching banner
   * inside the grid template. Banner shows after the user clicks the header
   * checkbox to select the whole page AND there are more matching rows
   * beyond the page; offers a one-click escalation to "select all matching"
   * via the existing `selectAllMatching()` imperative method.
   *
   * Set false to hide the banner entirely (e.g., consumers that provide
   * their own select-all UX via a toolbar). Even when false, the per-grid
   * `SsrmSelectAllPromptService` is still provided (cheap, idempotent).
   */
  @Input() showSelectAllBanner = true;
  /**
   * Stream of total matching count (typically the consumer's state-service
   * `itemTotal$`). Required when `showSelectAllBanner === true`. The grid
   * itself emits `itemTotal` via `(dataLoaded)` on every block fetch; the
   * consumer is expected to mirror it into a state service that the
   * banner subscribes to.
   */
  @Input() itemTotal$?: Observable<number>;
  /**
   * Stream of current selection count (typically the consumer's
   * state-service `selectedCount$`). Required when `showSelectAllBanner ===
   * true`. The consumer's `(selectionChanged)` handler is expected to
   * mirror `selected.size` into the state service, completing the round-
   * trip.
   */
  @Input() selectedCount$?: Observable<number>;
  /**
   * Sidebar configuration (column tool panel for visibility toggle and
   * reordering). Defaults to `CDW_DEFAULT_SIDEBAR` which puts the columns
   * panel on the left with search + drag-to-reorder enabled. Pass `false`
   * (cast as `CdwSidebarDef`) to hide the sidebar entirely; pass a custom
   * `SideBarDef` to override.
   *
   * Without this, users have no way to hide/show columns or change column
   * order in-app — which means the`onColumnVisible` refresh path
   * has no UI trigger.
   */
  @Input() cdwSidebarDef: CdwSidebarDef = CDW_DEFAULT_SIDEBAR;

  // === In-cell edit ===
  /**
   * When true, hides the embedded `<cdw-ae-filter-bar>` so the consumer
   * can render an alternate UI strip in its place (e.g., a Cancel + Save
   * Changes bar that surfaces only while edits are pending). Filter-bar
   * STATE (`currentFilters`, last emit) is preserved while hidden so
   * toggling back exposes it with the same selections.
   */
  @Input() hideFilterBar = false;
  /**
   * When true, AG-Grid starts inline editing on a single click (rather
   * than the default double-click). Default false (AG-Grid default) so
   * existing consumers don't change behavior.
   */
  @Input() singleClickEdit = false;
  /**
   * EDUS-2508: When true, left-clicking a non-editable cell surfaces a
   * "Permission restricted. View only." tooltip near the cursor. Mirrors
   * the legacy `cdw-search-grid`'s `showNonEditableTooltips` behavior so
   * consumers migrated from the legacy grid get parity feedback when a
   * read-only user clicks a cell expecting to edit.
   *
   * Default false — consumers opt in; the SSRM grid is generic and many
   * consumers won't have an editability concept to explain.
   */
  @Input() showNonEditableTooltips = false;
  /**
   * CSS color string applied as the background of cells modified in the
   * current edit session (tracked by `modifiedCellsByRow`, populated from
   * the AG-Grid `cellValueChanged` event). Default `#fef08a` (yellow).
   */
  @Input() modifiedCellColor = '#fef08a';

  // === Footer (selection actions strip) ===
  /**
   * Reflects whether the consumer is currently filtering the grid to its
   * selected IDs only. Drives the embedded `<lib-grid-footer>`'s
   * "Show Selected Only" / "Show All" link label and the consumer's
   * `idAllowList$` source-of-truth (set by the consumer when the toggle
   * fires; mirror back here so the link label tracks the actual state).
   *
   * The grid does NOT own filter scope itself — this is a presentation
   * input, identical in spirit to how `hideFilterBar` is consumer-driven.
   * Mutual exclusion with the consumer's "Show Changes Only" toggle is
   * enforced consumer-side (both write to the same `idAllowList$`).
   */
  @Input() showSelectedOnly = false;

  // === Wire-format reduction ===
  /**
   * Field paths that MUST appear in every block fetch's `selectFields`,
   * regardless of column visibility. Typical contents:
   *   - `id` (grid identity; `getRowId` reads it)
   *   - `isStale` (PreviewIcon cell renderer reads it from row data)
   *   - any fields the consumer reads from row data outside of cell
   *     renderers (e.g. `device-search` consumer reads `serialNumber`,
   *     `vendorId`, `status`, `vendorProperties.google.orgUnitPath` for
   *     audit-log enrichment).
   *
   * The grid unions this list with the colIds of currently-visible
   * non-virtual columns and forwards the result as `selectFields` to
   * `fetchSqlData(...)`. Backend (Phases 2/3) projects the response to
   * exactly those paths, dropping heavy fields like `rawVendorProperties`
   * — observed 50–90% wire payload reduction per row.
   *
   * Empty list (default) preserves the legacy "request full entities"
   * behavior: `fetchSqlData` won't include `selectFields` in its params.
   */
  @Input() alwaysRequiredFields: string[] = [];

  // === Augmentation layer ===
  /**
   * Stream of IDs (as strings) currently in the "updating" state. Each
   * fetched block has its rows augmented with `isUpdating: true` for IDs in
   * the latest emitted set, so the existing `PreviewIconComponent` cell
   * renderer (`data.isUpdating` → spinner) and `getRowClass: 'stale-row'`
   * styling pick up the state without any cell-renderer changes.
   *
   * When the set changes mid-flight (e.g. a job kicked off and pushed 200
   * IDs into the set), the component diffs the prev/next sets and surgically
   * `redrawRows` only the affected visible nodes — no full block refetch.
   *
   * Optional. Consumers that don't need updating-state augmentation simply
   * don't pass this input.
   */
  @Input() updatingIds$?: Observable<Set<string>>;
  /**
   * Stream of "is the SSRM selection lock active for this grid?" — drives
   * checkbox disabling (`isRowSelectable: () => false` while true) and a
   * host CSS class for visual gray-out + tooltip. Used by the stuck-job
   * failsafe consumer (device-search). Fires `true` while a bulk-action
   * job is in flight for this grid-session-uuid; the user cannot toggle
   * selection until the job terminates or is cancelled. Paging/sort/filter
   * remain interactive — only the selection set is frozen.
   *
   * Optional. Consumers without a lock concept simply don't pass this input.
   */
  @Input() selectionLockActive$?: Observable<boolean>;
  /**
   * Predicate evaluated per fetched row. When it returns true, the row is
   * augmented with `isUpdating: true` (same effect as appearing in
   * `updatingIds$`), driving the spinner via the existing PreviewIconComponent.
   * Used by the SSRM selection-lock failsafe to flag all currently-selected
   * rows as updating without materializing the full selected-id Set on the
   * client (which can be 500k for large server-side selections).
   *
   * The consumer typically passes a snapshot-aware predicate like
   * `(row) => row.isSelected && currentLockActive`. The grid evaluates it
   * on each block fetch in the SSRM datasource factory.
   *
   * Optional. Consumers without a lock concept simply don't pass this input.
   */
  @Input() selectedRowSpinnerPredicate?: (row: { id: string | number; isSelected?: boolean }) => boolean;
  /**
   * Stream of cells currently being saved by an
   * in-flight bulk-edit job. Map keyed by row id (string) → set of field
   * paths. Cells in the set get the `.saving-cell` CSS class applied via
   * the wrapped cellClass callback — drives the per-cell indeterminate
   * spinner visual (CSS pseudo-element).
   *
   * Consumer typically populates this when a save dispatches and clears
   * it on job completion. Distinct from `updatingIds$` (row-level visual
   * for force-refresh / cache-update flow): savingCells is per-cell and
   * scoped to the in-cell-edit save flow specifically.
   *
   * Optional. Consumers without an in-cell-edit save flow simply don't
   * pass this input.
   */
  @Input() savingCells$?: Observable<Map<string, Set<string>>>;
  /**
   * Stream of consumer-owned pending in-cell edits, keyed by row id (string)
   * → field path → new value. Forwarded to the SSRM datasource factory
   * which overlays each pending value onto the matching fetched row's
   * `node.data` BEFORE AG-Grid sees it. Effect: a user's unsaved edit
   * value persists across SSRM block evict + refetch — when the user
   * paginates away from a page they edited and returns, the cells still
   * show the edited values (the `.modified-cell` visual via
   * `modifiedCellsByRow` already persists; this stream takes care of the
   * underlying VALUE persistence).
   *
   * Field paths use dotted notation (e.g. `vendorProperties.google.notes`)
   * so the factory's overlay walks into nested objects.
   *
   * Optional. Consumers without an in-cell-edit flow simply don't pass
   * this input — rows pass through unchanged.
   */
  @Input() pendingEdits$?: Observable<Map<string, Map<string, unknown>>>;
  /**
   * EDUS-2509: snapshot of client-supplied row IDs to restrict the grid to.
   * When the consumer emits a non-empty array, the grid refreshes server-side
   * and the SSRM datasource forwards the IDs to `fetchSqlData` as
   * `idAllowList`. The data service translates that into the existing
   * `activeFilters: JSON.stringify(ids)` wire format which the backend's
   * `mapAssetIdsToQueryDto()` understands. Emit `undefined` to restore the
   * normal filter chain.
   *
   * Distinct from `[useSelectionAsFilter]` (boolean → server-side Redis
   * selection-set lookup) — that path can't serve the "Show changes only"
   * UX because cellChanges live purely client-side. The Phase 9 refactor
   * deleted this Input without a replacement for the changes-only path,
   * which is why the toggle in device-search regressed.
   */
  @Input() idAllowList$?: Observable<string[] | undefined>;
  // Phase 9 of EDUS-2485 deleted the deprecated `[selectedIds$]` and
  // `[fetchAllIds]` @Inputs that were temporarily kept as no-op shims
  // during the Phase 7→9 transition window. The server-side selection store
  // (callbacks below + [useSelectionAsFilter]) fully replaces them.
  // `[idAllowList$]` was also deleted but is restored above (EDUS-2509)
  // because the changes-only UX has no server-side state to look up.

  // === Server-side selection store callbacks (EDUS-2485 Phase 7) ===
  /**
   * Async callback invoked by `selectAllMatching()`. The consumer wires
   * this to a service method that POSTs the current filter to the server-
   * side selection store; the backend resolves the filter to ids and
   * adds them to the user's Redis selection set. The grid awaits the
   * response, then triggers `refreshServerSide({purge: true})` so the
   * next page-fetch returns rows with `isSelected: true` for the
   * newly-selected ids.
   *
   * Returns the new total selection count for footer display; element-cap
   * (>500k) rejections bubble as Errors that the consumer handles via
   * `(errorRetrievingData)` (snackbar, etc.).
   *
   * Optional — when omitted, `selectAllMatching()` logs a warning + no-op.
   */
  @Input() selectByFilter?: (
    filter: SelectedFilterField[]
  ) => Observable<{ count: number }>;
  /**
   * Async callback invoked by `deselectAllMatching()`. Mirror of
   * `selectByFilter` for the deselect-all-matching path. Same cap rule
   * applies (rejection bubbles through `(errorRetrievingData)`).
   *
   * Optional — when omitted, `deselectAllMatching()` logs a warning + no-op.
   */
  @Input() deselectByFilter?: (
    filter: SelectedFilterField[]
  ) => Observable<{ count: number }>;
  /**
   * Async callback invoked when individual checkbox clicks need to
   * propagate to the server-side selection store. The grid emits
   * `(selectionToggled)` per changed row; the CONSUMER is responsible
   * for batching these (typically via 250ms debounce → call this
   * callback with the batch of ids).
   *
   * NOT invoked directly by the grid library — wired here for symmetry
   * with `clearAllSelected` so the public callback API is grouped.
   * Optional.
   */
  @Input() selectByIds?: (ids: string[]) => Observable<{ count: number }>;
  /**
   * Async callback for batched deselect, mirror of `selectByIds`.
   * Optional.
   */
  @Input() deselectByIds?: (ids: string[]) => Observable<{ count: number }>;
  /**
   * Async callback invoked by `clearSelection()` and on filter changes.
   * Drops the entire user selection set on the server. The grid awaits
   * the response, then triggers `refreshServerSide({purge: true})` so
   * the next page-fetch returns rows with `isSelected: false` everywhere.
   *
   * Optional — when omitted, `clearSelection()` is a no-op (logs warning).
   */
  @Input() clearAllSelected?: () => Observable<unknown>;
  /**
   * "Show selected only" toggle. When true, the SSRM datasource forwards
   * `useSelectionAsFilter: true` to the consumer's `fetchSqlData`; the
   * data service translates that to a query string param and the backend
   * filters results to only rows in the user's Redis selection set.
   * Pagination + sort are honored within the filtered set.
   *
   * Default false (normal filter chain applies). Pass-through into the
   * datasource factory via `getUseSelectionAsFilter` callback.
   */
  @Input() useSelectionAsFilter = false;

  // === Filter bar contract (drop-in replacement for CdwSearchGridComponent) ===
  /** Field definitions for the embedded filter bar. */
  @Input() filterBarSettings: FilterBarField[] = [];
  /** Initial filter selections (e.g. restored from URL/sessionStorage). */
  @Input() initialSelectedFilterFields?: SelectedFilterField[];
  /** Identity for the filter set (passed back in `(filtersChange)` events
   * so consumers managing multiple filter bars can disambiguate). */
  @Input() filterBarId = 'default';
  /** Optional observable of field-name allow-list — restricts which
   * `filterBarSettings` entries are visible. Pass-through to filter bar. */
  @Input() fieldMask$?: Observable<string[]>;

  // === Outputs ===
  /** Fires on row click; consumers typically navigate to a row detail view. */
  @Output() rowClicked: EventEmitter<RowClickedEvent> = new NgEventEmitter();
  /**
   * Fires on cell click with the AG-Grid `CellClickedEvent`
   * payload. Use when navigation should be gated to a specific column (e.g.
   * an eye/View column) — `event.column.getColId()` discriminates which
   * column the user actually clicked. `(rowClicked)` doesn't include column
   * info (AG-Grid limitation) so it can't distinguish a click on the View
   * column from a click on a data cell.
   */
  @Output() cellClicked: EventEmitter<CellClickedEvent> = new NgEventEmitter();
  /**
   * Fires after every successful block fetch, with the total row count
   * reported by the backend and the `hasStaleData` flag from the asset cache
   * `meta`. Consumers mirror `itemTotal` to a header counter and use
   * `hasStaleData` to drive a "data refresh in progress" badge.
   */
  @Output() dataLoaded: EventEmitter<{
    itemTotal: number;
    hasStaleData?: boolean;
  }> = new NgEventEmitter();
  /**
   * Fires when the SSRM datasource's `getRows` callback errors. AG-Grid is
   * already informed via `params.fail()`; this output is for consumer-side
   * error reporting (snackbar, retry button, etc.).
   */
  @Output() errorRetrievingData: EventEmitter<Error> = new NgEventEmitter();
  /**
   * Fires once after the first block of rows is rendered. Consumers gate
   * "wait until grid has data" workflows on this (e.g. the Wipe Profiles
   * auto-trigger). Idempotent — only fires once per component
   * lifetime regardless of how many subsequent block fetches occur.
   */
  @Output() firstDataRendered: EventEmitter<void> = new NgEventEmitter();
  /**
   * Filter bar emit. Payload shape `{filters, filterBarId}` matches the
   * existing `CdwSearchGridComponent` contract so consumer templates are a
   * drop-in element-name swap.
   */
  @Output() filtersChange: EventEmitter<{
    filters: SelectedFilterField[];
    filterBarId: string;
  }> = new NgEventEmitter();
  // Phase 9 of EDUS-2485 deleted the deprecated `(selectionChanged)` Output
  // (formerly emitted Set<string>); use `(selectionToggled)` below for
  // per-row delta events.
  /**
   * Fires per individual row checkbox change. Payload `{rowId, op}`
   * where op is `'select'` or `'deselect'`. Consumer typically debounces
   * (250 ms) + batches by op, then calls the appropriate
   * `selectByIds`/`deselectByIds` callback with the batch.
   *
   * For batch operations like the header-checkbox "select all visible
   * rows" click, AG-Grid fires its internal `selectionChanged` event
   * once at the end of the batch; the grid's onSelectionChanged handler
   * computes the per-row diff and emits one `selectionToggled` event
   * per changed row. The consumer's debounce collapses the burst.
   *
   * Per-row state is also optimistically updated on `node.data.isSelected`
   * so the visual stays consistent with what the consumer's batched
   * callback will eventually persist.
   */
  @Output() selectionToggled: EventEmitter<{
    rowId: string;
    op: 'select' | 'deselect';
  }> = new NgEventEmitter();
  /**
   * Forwards AG-Grid's per-cell `cellValueChanged` event when the user
   * commits an edit. Consumer pushes the new value into its cellChanges
   * Map keyed by row id + field. The grid also tracks the touched cell
   * internally (drives the modified-cell background) but does NOT own
   * the canonical edit collection — consumer is the source of truth.
   */
  @Output() cellValueChanged: EventEmitter<CellValueChangedEvent> =
    new NgEventEmitter();
  /**
   * AG-Grid `cellEditingStarted` re-emit. Consumers track this to enter
   * "edit mode" UX (e.g., showing Cancel + Save changes buttons) on first
   * interaction rather than waiting until the user has actually committed
   * a value change.
   */
  @Output() cellEditingStarted: EventEmitter<CellEditingStartedEvent> =
    new NgEventEmitter();
  /**
   * AG-Grid `cellEditingStopped` re-emit. Pairs with `cellEditingStarted`
   * for tracking an "is currently editing" flag on the consumer side.
   */
  @Output() cellEditingStopped: EventEmitter<CellEditingStoppedEvent> =
    new NgEventEmitter();
  /**
   * Fires when the user clicks the footer's "Show Selected Only" /
   * "Show All" toggle. Payload is the desired NEXT state (true =
   * activating Show Selected Only). Consumer is expected to snapshot the
   * current selection into `idAllowList$` (or clear it) and mirror the
   * value back via `[showSelectedOnly]` so the link label tracks state.
   */
  @Output() showSelectedOnlyChange: EventEmitter<boolean> =
    new NgEventEmitter();
  /**
   * Fires when the in-flight cell editor's value transitions between
   * "matches original" and "differs from original" — i.e., the cell
   * goes dirty during edit, BEFORE the user commits. AG-Grid's native
   * `cellValueChanged` only fires post-commit (blur / Enter / Tab),
   * so consumers wanting an immediately-enabled "Save" button while a
   * cell is being typed need this earlier signal.
   *
   * Emits `true` on the first character that diverges from the
   * snapshotted original; emits `false` if subsequent edits restore
   * the original value (delete + retype to same string). Always emits
   * `false` on `cellEditingStopped` regardless of commit outcome.
   *
   * Consumer pattern: `(cellEditingDirtyChanged)="isEditingDirty=$event"`
   * then OR it into the Save button's disabled gate.
   */
  @Output() cellEditingDirtyChanged: EventEmitter<boolean> =
    new NgEventEmitter();
  /**
   * Fires after every page-fetch when the response carries a server-side
   * selection count (EDUS-2484, Phase 6+). Drives the consumer's footer /
   * banner count display. NOT fired when the response lacks `selectionCount`
   * (legacy non-selection-aware backend, OR no `viewKey` minted a session
   * UUID, OR the data service skipped attaching the `x-cdw-grid-session`
   * header) — the consumer can use the absence of emit to mean "selection
   * store not engaged for this view".
   */
  @Output() selectionCount: EventEmitter<number> = new NgEventEmitter();
  /**
   * Fires when the user clicks the per-cell undo overlay button on an
   * edited cell (rendered by `EditableCellWithUndoComponent`). Payload
   * is `{ rowId, field }` identifying which cell was reverted.
   *
   * The grid has ALREADY reverted `node.data` to the original value and
   * dropped the entry from its internal `modifiedCellsByRow` tracking by
   * the time this fires — the consumer's handler is only responsible for
   * dropping the matching entry from its own `cellChanges` payload Map
   * (so the next save dispatch and any pendingEdits$ overlay no longer
   * carry the reverted value), recomputing the change count, and
   * recomputing any allow-list filter that derives from cellChanges
   * (e.g., "Show changes only").
   */
  @Output() cellUndoClicked: EventEmitter<{ rowId: string; field: string }> =
    new NgEventEmitter();

  /**
   * Per-grid-instance prompt service (provided in this component's
   * `providers` array). Set/cleared by `SsrmHeaderCheckboxComponent` on
   * page-select-all clicks; subscribed to by `SsrmSelectAllBannerComponent`
   * to render the banner. Also cleared on filter change + during
   * `selectAllMatching()` busy state.
   */
  constructor(
    private readonly promptService: SsrmSelectAllPromptService,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly liveAnnouncer: LiveAnnouncer,
    // Optional: consumers that want column-layout persistence provide this
    // token (spog-client binds it to its localStorage-backed
    // `ComponentStateService`). When absent, save/restore are no-ops.
    @Optional()
    @Inject(CDW_SAVE_COMPONENT_STATE_SERVICE_TOKEN)
    private readonly saveStateService: ICdwSaveComponentStateService<PersistedGridState> | null = null
  ) {}

  /**
   * Guards against the save↔restore feedback loop: `applyColumnState`
   * during restore fires `columnVisible` / `columnMoved` synchronously
   * (gridOptions sets `suppressAsyncEvents: true`), which would otherwise
   * re-persist the state we just applied. Set for the duration of the
   * programmatic apply only.
   */
  private isRestoringColumnState = false;

  /**
   * Set while AG-Grid rebuilds columns from a runtime `columnDefs`
   * reassignment (e.g. device-search rebuilding date formatters when the
   * date/time setting changes). That rebuild re-applies each NEW colDef's
   * `flex`/`hide` to the reused columns — discarding the user's resized
   * widths and restored visibility, and resetting order — and fires
   * `columnResized` / `columnVisible` synchronously. Without this guard those
   * events would persist the reset layout and clobber the saved one. We
   * suppress persistence for the rebuild window (set in
   * `ngOnChanges('columnDefs')`) and re-apply the saved layout once the new
   * columns have loaded (`onNewColumnsLoaded`).
   */
  private columnDefsReapplyPending = false;

  /**
   * Filter selections owned by this component. Updated on every emit from the
   * embedded filter bar; read by the SSRM datasource on every `getRows` call.
   * Seeded from `initialSelectedFilterFields` in `ngOnChanges`.
   */
  private currentFilters: SelectedFilterField[] = [];

  /**
   * Latest snapshot of `updatingIds$`. Read by the SSRM datasource factory on
   * every `getRows` call to augment row data; also retained between emissions
   * so `redrawForUpdatingChange(prev, next)` can compute the symmetric
   * difference and only redraw rows whose state actually changed.
   */
  private currentUpdatingIds: Set<string> = new Set();
  /**
   * Tracks the current state of the selection lock. Mutated by the
   * `selectionLockActive$` subscription; read by `isRowSelectable` (declared
   * in gridOptions) on every selection toggle attempt. Defaults false — when
   * no `selectionLockActive$` is provided, the grid behaves as before.
   */
  protected currentLockActive = false;

  /**
   * Active "Permission restricted" tooltip element appended to document.body.
   * Single-instance — a fresh click clears the previous one. See
   * showClickTooltip / removeClickTooltip. EDUS-2508.
   */
  private activeClickTooltip: HTMLElement | null = null;
  /**
   * mousemove handler attached to the clicked cell while the tooltip is
   * visible, so the tooltip tracks the cursor. Captured as a field so
   * removeClickTooltip can pass the SAME reference to removeEventListener.
   */
  private tooltipMouseMoveHandler: ((e: Event) => void) | null = null;
  /**
   * mouseleave handler attached to the clicked cell while the tooltip is
   * visible. Removes the tooltip when the user moves off the cell.
   */
  private tooltipMouseLeaveHandler: (() => void) | null = null;

  /**
   * Latest snapshot of `savingCells$`. Read by the wrapped cellClass
   * callback to apply the `.saving-cell` class to cells currently being
   * persisted by an in-flight bulk-edit job. Mutated only via the
   * subscription set up in `ngOnChanges('savingCells$')`.
   */
  private currentSavingCells: Map<string, Set<string>> = new Map();

  /**
   * Latest snapshot of `pendingEdits$`. Read by the SSRM datasource
   * factory on every `getRows` call to overlay user-edited values onto
   * freshly-fetched rows.
   */
  private currentPendingEdits: Map<string, Map<string, unknown>> = new Map();

  /**
   * EDUS-2509: snapshot of the consumer's `idAllowList$` emission. Read
   * by the SSRM datasource factory on every getRows call to forward into
   * `fetchSqlData` as `idAllowList`. `undefined` (the default) means the
   * normal filter chain applies.
   */
  private currentIdAllowList: string[] | undefined = undefined;

  /**
  // Phase 7 of EDUS-2485: `currentSelectedIds` was a snapshot backing the
  // now-deprecated `[selectedIds$]` @Input. Per-row selection state lives
  // on `node.data.isSelected` (server-provided by Phase 3); "show selected
  // only" is driven by the boolean `[useSelectionAsFilter]` @Input. The
  // legacy @Input declaration remains as a deprecated shim so consumers
  // compile, but no field-level state mirrors its emissions anymore.
  //
  // `currentIdAllowList` (above) was deleted alongside in Phase 9, then
  // restored by EDUS-2509 — the "Show changes only" UX can't ride the
  // server-side selection store because cellChanges live purely client-side.

  /**
   * Observable wrapper around `initialSelectedFilterFields` for consumption by
   * the embedded `<cdw-ae-filter-bar>` component (which expects an Observable
   * input, not a plain array). Re-emits when the input array reference changes.
   */
  protected initialSelectedFilterFields$ = new BehaviorSubject<
    SelectedFilterField[]
  >([]);

  // === In-cell edit internal state ===
  /**
   * RowId → field path → ORIGINAL value (the cell's value before the user's
   * first edit in the current edit session). Populated from `(cellValueChanged)`
   * with set-if-absent semantics — only the first edit per cell captures the
   * original, so subsequent edits to the same cell don't overwrite it with
   * the value-before-the-second-edit. Drives:
   *
   *   - The `.modified-cell` CSS class (via the wrapped `cellClass` callback,
   *     which checks `.has(field)`).
   *   - The per-cell undo overlay button (rendered by
   *     `EditableCellWithUndoComponent` for any cell whose field appears
   *     in this map). Click → revert via `setDataValue(field, originalValue,
   *     'cdw-undo')` and emit `(cellUndoClicked)`.
   *
   * Cleared on `exitEditMode()` and on customer / filter-change reset
   * (consumer-driven).
   */
  private modifiedCellsByRow = new Map<string, Map<string, unknown>>();
  /**
   * Original `editable` value per editable colId (boolean true OR
   * function). The wrapped callback delegates to this so the consumer's
   * per-cell editable function (e.g., permission checks) is preserved.
   */
  private originalEditables = new Map<string, CdwColDef['editable']>();
  /**
   * Derived array bound to AG-Grid in place of the raw `columnDefs` @Input.
   * Re-derived in `ngOnChanges('columnDefs')`; wrapping installs the
   * `editable`/`cellClass` callbacks (saving-cell + modified-cell visuals,
   * saving-cell editability gate) and the `headerComponent` for cols with
   * `headerIcon`.
   */
  protected wrappedColumnDefs: CdwColDef[] = [];

  protected gridApi?: GridApi<T>;
  /**
   * Captured in `onGridReady` so `computeSelectFields()` projection can read
   * column visibility state. AG-Grid 28 keeps `columnApi` separate from
   * `gridApi`; v30+ merges them. Reaching for it via the ready event is
   * the supported v28 pattern.
   */
  protected columnApi?: ColumnApi;
  /**
   * AG-Grid options. Built in `ngOnInit` (NOT as a class-field initializer)
   * so the construction reads bound `@Input` values rather than class-field
   * defaults. Class field initializers run during construction, BEFORE
   * Angular sets `@Input` bindings — so values like `singleClickEdit` and
   * `paginationPageSize` would silently fall back to their declared defaults
   * if built at construction time. The same lifecycle pitfall is documented
   * on `SsrmSelectAllBannerComponent.viewModel$` (also moved to ngOnInit
   * for the same reason).
   *
   * `<ag-grid-angular>` reads this via `[gridOptions]="gridOptions"` during
   * the parent's view-init, which happens AFTER ngOnInit, so AG-Grid sees
   * the correctly-built object.
   */
  protected gridOptions!: GridOptions;
  private readonly destroy$ = new Subject<void>();
  private hasEmittedFirstDataRendered = false;

  /**
   * Per-page selection count surfaced to the footer so it can render the
   * "(across pages)" disclosure when the global `selectedCount` exceeds
   * what's selected on the current page. Recomputed on `selectionChanged`,
   * `modelUpdated`, and `paginationChanged`. The footer compares this
   * against `selectedCount$` and renders the hint when total > per-page.
   */
  private readonly selectedOnPageCountSubject = new BehaviorSubject<number>(0);
  protected selectedOnPageCount$: Observable<number> =
    this.selectedOnPageCountSubject.asObservable();

  /**
   * SSRM datasource. AG-Grid calls `getRows` whenever it needs a block.
   * Construction is delegated to `buildSqlSsrmDatasource(...)` so the
   * translation logic can be unit-tested in isolation from the Angular
   * runtime — see `sql-ssrm-datasource.factory.ts` and its spec. Each callback
   * uses `() => this.x` to read live values at fetch time, so consumers can
   * mutate filters or swap the data service after construction without
   * stale closures.
   *
   * NOTE: this implementation deliberately avoids the select-all / partial-
   * search interleaving from the prior abandoned SSRM code in
   * `CdwSearchGridComponent`. Selection state belongs to the consumer
   * and is NOT entangled with the data-fetch path.
   */
  private readonly sqlSsrmDatasource: IServerSideDatasource =
    buildSqlSsrmDatasource<T>({
      getCurrentFilters: () => this.currentFilters,
      getDataService: () => this.cdwSearchGridDataService,
      getUpdatingIds: () => this.currentUpdatingIds,
      getSelectedRowSpinnerPredicate: () =>
        // Only forward the predicate while the lock is active — otherwise the
        // pure spinner-from-selected behavior would persist after release.
        // Cast: consumer-facing input is typed `{id, isSelected?}` for
        // ergonomics; factory takes `unknown` because it's generic over T.
        // The widening cast is safe — the factory only invokes the predicate.
        this.currentLockActive
          ? (this.selectedRowSpinnerPredicate as
              | ((row: unknown) => boolean)
              | undefined)
          : undefined,
      getSelectFields: () => this.computeSelectFields(),
      getPendingEdits: () => this.currentPendingEdits,
      // EDUS-2509: restored alongside useSelectionAsFilter — the two cover
      // independent "filter the grid to a subset" mechanisms:
      //   - useSelectionAsFilter (boolean) → server-side Redis lookup
      //   - getIdAllowList (string[]) → client-supplied ids, used for
      //     "Show changes only" where the ids live in the consumer's
      //     cellChanges Map and have no server-side representation.
      getIdAllowList: () => this.currentIdAllowList,
      getUseSelectionAsFilter: () => this.useSelectionAsFilter,
      getGridSessionId: () => this.gridSessionId,
      destroy$: this.destroy$,
      getItemLabel: () => this.itemLabel,
      onDataLoaded: (info) => this.dataLoaded.emit(info),
      onSelectionCount: (count) => this.selectionCount.emit(count),
      onError: (err) => this.errorRetrievingData.emit(err),
    });

  /**
   * Per-grid-instance session UUID. Minted in `ngOnInit` from
   * `sessionStorage[grid-session:${viewKey}]` (or freshly generated if
   * absent) so two tabs of the same page get distinct sessions and a
   * page reload within the same tab restores the prior session. Used by
   * the SSRM datasource factory to send the `gridSessionId` param into
   * `fetchSqlData(...)`; the consumer's data service translates that to
   * the `x-cdw-grid-session` HTTP header. Empty string when sessionStorage
   * is unavailable (test/SSR environments) — the data service then skips
   * attaching the header and the backend takes its non-selection-aware
   * code path.
   *
   * Public so consumers can read it for the new selection callbacks (the
   * 5 callback @Inputs all need to pass the sessionId to their data
   * service methods so the same Redis key is used for both the page-fetch
   * header attachment and the explicit selection-mutation calls).
   */
  public gridSessionId = '';

  /**
   * Per-input subscription handles. Held so `ngOnChanges` can tear down the
   * old subscription before subscribing to a replacement observable (the
   * alternative — relying solely on `takeUntil(destroy$)` — would leak
   * emissions from the prior observable into the new state).
   */
  private updatingIdsSubscription?: { unsubscribe: () => void };
  // selectedIdsSubscription removed in Phase 7 of EDUS-2485 (deprecated input no longer subscribed).
  private savingCellsSubscription?: { unsubscribe: () => void };
  private pendingEditsSubscription?: { unsubscribe: () => void };
  private selectionLockSubscription?: { unsubscribe: () => void };
  // EDUS-2509: restored. Phase 7 of EDUS-2485 deleted this subscription
  // when the deprecated `[idAllowList$]` Input was retired, but the
  // "Show changes only" UX (which doesn't have a server-side state to
  // look up) needs it back.
  private idAllowListSubscription?: { unsubscribe: () => void };
  /**
   * MutationObserver that re-injects sidebar pencil icons whenever
   * AG-Grid rebuilds the column tool panel DOM (sidebar open/close,
   * column reorder, columnDefs replace, theme switch). Scoped to the
   * grid's own `.ag-root-wrapper` via `elementRef` so multiple grids
   * on the same page don't see each other's mutations. Disconnected
   * in `ngOnDestroy`.
   */
  private columnSelectorObserver?: MutationObserver;

  /**
   * Snapshotted original cell value at the moment `cellEditingStarted`
   * fired. Compared against the live editor value on every input event
   * to compute the dirty flag. Reset to `undefined` when no editor is
   * open. `unknown` because cells can hold strings / numbers / null /
   * arbitrary objects depending on the field.
   */
  private editingOriginalValue: unknown = undefined;
  /**
   * Latest dirty-state snapshot — held so the input listener can detect
   * transitions and emit `cellEditingDirtyChanged` only on actual
   * change (not on every keystroke).
   */
  private isEditingDirty = false;
  /**
   * Bound input-event handler installed on the grid host while a cell
   * editor is open. Held so we can `removeEventListener` the SAME
   * function reference in `cellEditingStopped` (anonymous functions
   * can't be removed). The `input` event bubbles from any text editor's
   * `<input>` inside the cell, so a single host-level listener covers
   * all editable columns without per-editor wiring.
   */
  private editorInputListener?: (event: Event) => void;

  /**
   * The currently-active page size. Seeded in `ngOnInit` from the persisted
   * blob (falling back to `paginationPageSize`), updated on user selection.
   * Drives the footer selector's highlighted option and the value written
   * back to persistence.
   */
  public currentPageSize = 25;

  public ngOnInit(): void {
    // Mint or restore the per-grid-instance session UUID. Keyed by viewKey so
    // two grids on the same page in the same tab get distinct sessions;
    // sessionStorage scope means a page reload within the same tab restores
    // the prior session. Wrapped in try/catch because sessionStorage can
    // throw in private-browsing or sandboxed contexts — falling back to an
    // empty string keeps the grid functional (selection store just won't
    // engage; the chain is full backwards-compat without the header).
    this.gridSessionId = mintOrRestoreSessionId(this.viewKey);

    // Seed the active page size from persistence BEFORE building gridOptions,
    // so the very first block fetch already uses the restored size (no
    // default-then-refetch). Falls back to the `paginationPageSize` @Input
    // when there's no valid persisted value.
    this.currentPageSize = this.readRestoredPageSize() ?? this.paginationPageSize;

    // Construct gridOptions HERE (not as a class-field initializer) so the
    // build reads bound @Input values (singleClickEdit, paginationPageSize,
    // cacheBlockSize, maxBlocksInCache, noRowsOverlayText). See the
    // gridOptions field-doc above for the lifecycle reasoning.
    this.gridOptions = this.buildGridOptions();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // Seed currentFilters and re-broadcast to the embedded filter bar whenever
    // the consumer reassigns initialSelectedFilterFields (e.g. URL restore,
    // workflow auto-trigger from dashboard).
    if ('initialSelectedFilterFields' in changes) {
      const next = this.initialSelectedFilterFields ?? [];
      this.currentFilters = next;
      this.initialSelectedFilterFields$.next(next);
      // If grid is already initialized, force a block refresh against the new
      // filters so the displayed rows match. (No-op pre-onGridReady.)
      this.gridApi?.refreshServerSide({ purge: true });
    }

    if ('updatingIds$' in changes) {
      this.updatingIdsSubscription?.unsubscribe();
      this.currentUpdatingIds = new Set();
      this.updatingIdsSubscription = this.updatingIds$
        ?.pipe(takeUntil(this.destroy$))
        .subscribe((nextSet) => {
          const prevSet = this.currentUpdatingIds;
          this.currentUpdatingIds = nextSet;
          this.redrawForUpdatingChange(prevSet, nextSet);
        });
    }

    if ('selectionLockActive$' in changes) {
      this.selectionLockSubscription?.unsubscribe();
      this.currentLockActive = false;
      this.selectionLockSubscription = this.selectionLockActive$
        ?.pipe(takeUntil(this.destroy$))
        .subscribe((nextActive) => {
          const prevActive = this.currentLockActive;
          this.currentLockActive = nextActive;
          if (prevActive === nextActive) return;
          // Spinner overlay on selected rows — predicate gate flips with
          // the lock; in-cache blocks need explicit mutation since the
          // SSRM factory's augmentation only runs on FRESH block fetches.
          this.applyLockSpinnerToCachedRows(nextActive);
          // Header re-render so the custom SsrmHeaderCheckboxComponent
          // re-evaluates against the new lock state. The row checkbox
          // gray-out + pointer-events:none is pure CSS via the host
          // class `.cdw-ssrm-grid--selection-locked`, so no row redraw
          // is needed for that.
          this.gridApi?.refreshHeader();
          // ARIA: keyboard / AT users get an explicit announcement on
          // both edges of the lock toggle. LiveAnnouncer keeps a single
          // aria-live region mounted at the document level, so the
          // release message is reliably delivered (a *ngIf-removed
          // visually-hidden span cannot announce its own removal).
          this.liveAnnouncer.announce(
            nextActive
              ? 'Selection locked while bulk action runs.'
              : 'Selection unlocked.',
            'polite'
          );
        });
    }

    // [selectedIds$] subscription removed in Phase 7 of EDUS-2485 — per-row
    // selection state now lives on `node.data.isSelected` (server-provided,
    // populated by Phase 3's enrichment) instead of being mirrored from a
    // consumer-supplied Set. The @Input declaration is kept as a deprecated
    // shim so legacy consumers compile during the Phase 7→9 transition;
    // emissions are silently ignored.

    if ('savingCells$' in changes) {
      this.savingCellsSubscription?.unsubscribe();
      this.currentSavingCells = new Map();
      this.savingCellsSubscription = this.savingCells$
        ?.pipe(takeUntil(this.destroy$))
        .subscribe((nextMap) => {
          this.currentSavingCells = nextMap;
          // Force a full cell refresh so cellClass re-evaluates and the
          // .saving-cell visual applies (or drops) immediately. Cheap —
          // AG-Grid only re-renders cells whose class actually changed.
          this.gridApi?.refreshCells({ force: true });
        });
    }

    if ('pendingEdits$' in changes) {
      this.pendingEditsSubscription?.unsubscribe();
      this.currentPendingEdits = new Map();
      this.pendingEditsSubscription = this.pendingEdits$
        ?.pipe(takeUntil(this.destroy$))
        .subscribe((nextMap) => {
          this.currentPendingEdits = nextMap;
          // No proactive refresh needed for ON-SCREEN cells: AG-Grid's
          // editor commits the new value directly to `node.data` on
          // cellValueChanged, so the visible cell already shows it.
          // The factory snapshot is for FUTURE block fetches (after
          // eviction or sort/filter change). Defense-in-depth refresh
          // is cheap and covers any out-of-band edits the consumer
          // may push (e.g., test harness mutating the Map directly).
          this.gridApi?.refreshCells({ force: true });
        });
    }

    // EDUS-2509: restored the [idAllowList$] subscription (deleted in
    // Phase 7 of EDUS-2485). The "Show selected only" UX migrated to the
    // server-side selection-store path ([useSelectionAsFilter] + backend
    // Redis lookup), but the "Show changes only" UX has no server-side
    // state to look up — cellChanges live purely client-side — so it
    // still needs the client-supplied id array path. Snapshot the latest
    // emission into `currentIdAllowList`, then refresh server-side so the
    // datasource factory picks it up on the next fetch.
    if ('idAllowList$' in changes) {
      this.idAllowListSubscription?.unsubscribe();
      this.currentIdAllowList = undefined;
      this.idAllowListSubscription = this.idAllowList$
        ?.pipe(takeUntil(this.destroy$))
        .subscribe((next) => {
          this.currentIdAllowList = next;
          // Purge so cached blocks built against the previous filter mode
          // don't bleed into the new view. Same shape as the
          // useSelectionAsFilter refresh below. No-op pre-onGridReady.
          this.gridApi?.refreshServerSide({ purge: true });
        });
    }

    if ('useSelectionAsFilter' in changes) {
      // Toggle changed — purge the cache and re-fetch from block 0 against
      // the new mode. Same shape as the legacy idAllowList$ refresh.
      // No-op pre-onGridReady.
      this.gridApi?.refreshServerSide({ purge: true });
    }

    // Re-wrap columnDefs whenever the consumer reassigns them. Editable
    // colDefs get their `editable` + `cellClass` replaced with callbacks
    // that close over `modifiedCellsByRow` + `currentSavingCells`; cols
    // with `headerIcon` get a `headerComponent` wired in.
    if ('columnDefs' in changes) {
      this.wrappedColumnDefs = this.wrapColumnDefs(this.columnDefs);
      // If the sidebar is already open, AG-Grid rebuilds its column rows
      // when columnDefs reassign — kick off a fresh injection pass so the
      // pencil icons land on the new rows. The MutationObserver also
      // catches this, but the explicit call shaves a frame and covers
      // the no-mutation edge case (e.g., identical column set rebuilt
      // by reference change).
      this.injectColumnSelectorIcons();

      // A runtime columnDefs reassignment (the device-search date/time
      // setting rebuilds date formatters → new columnDefs) makes AG-Grid
      // recreate columns and re-apply each new colDef's flex/hide, which
      // discards the user's resized widths + restored visibility and resets
      // order — and fires columnResized/columnVisible that would persist
      // (clobber) the reset. Suppress persistence for the rebuild and
      // re-apply the saved layout once the new columns have loaded (see
      // `onNewColumnsLoaded`). The `columnApi` guard inherently skips the
      // initial binding: `onGridReady` (which assigns `columnApi` and does
      // the first restore) only runs after the first columnDefs are applied,
      // so a set `columnApi` means this is a genuine post-init reassignment.
      if (this.saveStateService && this.columnApi) {
        this.columnDefsReapplyPending = true;
      }
    }

    // Mirror modifiedCellColor into a CSS custom property on the host so
    // the .modified-cell SCSS rule picks it up. Re-applied on every
    // change so consumers can swap themes dynamically.
    if ('modifiedCellColor' in changes) {
      this.elementRef.nativeElement.style.setProperty(
        '--ssrm-modified-cell-color',
        this.modifiedCellColor
      );
    }
  }

  public ngOnDestroy(): void {
    this.updatingIdsSubscription?.unsubscribe();
    this.savingCellsSubscription?.unsubscribe();
    this.selectionLockSubscription?.unsubscribe();
    this.pendingEditsSubscription?.unsubscribe();
    this.idAllowListSubscription?.unsubscribe();
    this.columnSelectorObserver?.disconnect();
    if (this.editorInputListener) {
      this.elementRef.nativeElement.removeEventListener(
        'input',
        this.editorInputListener
      );
      this.editorInputListener = undefined;
    }
    // EDUS-2508: drop any active "Permission restricted" tooltip + its
    // cell-attached listeners so a grid unmount doesn't leak a floating
    // div on document.body.
    this.removeClickTooltip();
    this.destroy$.next();
    this.destroy$.complete();
    this.initialSelectedFilterFields$.complete();
  }

  /**
   * Surgically redraw only the visible rows whose `isUpdating` state changed
   * between the previous and next sets. Mutates `node.data.isUpdating` in
   * place (which is safe because AG-Grid's row nodes own the row data
   * references) and then calls `gridApi.redrawRows({rowNodes})` only for the
   * affected nodes. No network traffic; no full block refetch; no flicker on
   * unaffected rows.
   *
   * The symmetric difference (`prev XOR next`) is the set of IDs whose state
   * flipped — additions need `isUpdating = true`, removals need `isUpdating
   * = false`.
   */
  /**
   * Walk all in-cache row nodes and apply the lock-derived `isUpdating`
   * mutation. Called when `selectionLockActive$` toggles so the visual
   * effect is immediate on rows that were fetched BEFORE the lock activated
   * (the SSRM datasource factory's augmentation pipeline only runs on fresh
   * block fetches; in-cache rows would otherwise render with stale
   * `isUpdating: undefined` until the user paginates away and back).
   *
   * On lock activation: sets `isUpdating: true` on every row whose
   * `selectedRowSpinnerPredicate` returns true (typically `row.isSelected`).
   * On lock release: clears `isUpdating` from rows where the predicate
   * was the source — but PRESERVES `isUpdating` on rows that are also in
   * the explicit `updatingIds` Set, so the two systems compose cleanly.
   *
   * No-op when the predicate isn't supplied (consumers that opt out of the
   * lock UX entirely).
   */
  private applyLockSpinnerToCachedRows(lockActive: boolean): void {
    if (!this.gridApi) return;
    const predicate = this.selectedRowSpinnerPredicate;
    if (!predicate) return;
    const updatingIds = this.currentUpdatingIds;

    const nodesToRedraw: RowNode<T>[] = [];
    this.gridApi.forEachNode((node) => {
      if (!node.data) return;
      const data = node.data as { id: string | number; isSelected?: boolean; isUpdating?: boolean };
      const id = String(data.id);
      let nextIsUpdating: boolean;
      if (lockActive) {
        // Lock active: predicate-true rows get the spinner. Rows already in
        // updatingIds keep theirs (predicate-true OR set-membership).
        nextIsUpdating = predicate(data) || updatingIds.has(id);
      } else {
        // Lock released: only updatingIds rows keep the spinner. Everything
        // else clears (the predicate would return false anyway since the
        // consumer's gate checks lockActive when forwarding the predicate).
        nextIsUpdating = updatingIds.has(id);
      }
      const prev = !!data.isUpdating;
      if (prev !== nextIsUpdating) {
        data.isUpdating = nextIsUpdating;
        nodesToRedraw.push(node);
      }
    });
    if (nodesToRedraw.length > 0) {
      this.gridApi.redrawRows({ rowNodes: nodesToRedraw });
    }
  }

  private redrawForUpdatingChange(
    prevSet: Set<string>,
    nextSet: Set<string>
  ): void {
    if (!this.gridApi) return;
    const changedIds = new Set<string>();
    for (const id of prevSet) if (!nextSet.has(id)) changedIds.add(id);
    for (const id of nextSet) if (!prevSet.has(id)) changedIds.add(id);
    if (changedIds.size === 0) return;

    const predicate = this.selectedRowSpinnerPredicate;
    const lockActive = this.currentLockActive;
    const nodesToRedraw: RowNode<T>[] = [];
    this.gridApi.forEachNode((node) => {
      if (!node.data) return;
      const id = String(node.data.id);
      if (!changedIds.has(id)) return;
      // Compose both signals: a row is "updating" if it's in the explicit
      // set OR (lock is active AND the predicate matches). This prevents an
      // updatingIds clear from blanking the spinner on rows that should
      // remain spinning because the lock is still active. Mutate in place;
      // AG-Grid's getRowId-keyed row nodes own this reference.
      const data = node.data as { id: string | number; isSelected?: boolean; isUpdating?: boolean };
      const fromSet = nextSet.has(id);
      const fromLock = lockActive && predicate ? predicate(data) : false;
      data.isUpdating = fromSet || fromLock;
      nodesToRedraw.push(node);
    });
    if (nodesToRedraw.length > 0) {
      this.gridApi.redrawRows({ rowNodes: nodesToRedraw });
    }
  }

  public onGridReady(event: GridReadyEvent<T>): void {
    this.gridApi = event.api as GridApi<T>;
    this.columnApi = event.columnApi;
    // Restore the user's saved column layout BEFORE wiring the datasource so
    // the very first block fetch's `computeSelectFields()` already reflects
    // restored visibility — no initial fetch with default columns followed
    // by a wasteful refetch. Safe to fire the resulting `columnVisible`
    // events here: `onColumnVisible`'s `refreshServerSide` is a no-op until
    // the datasource is set on the next line.
    this.restoreColumnState();
    this.gridApi.setServerSideDatasource(this.sqlSsrmDatasource);
    // Sidebar pencil icons (CdwColDef.columnSelectorIcon) live OUTSIDE
    // AG-Grid's column tool panel API surface — there's no v28 hook to
    // contribute custom DOM into a column-list row. The legacy grid's
    // proven workaround is DOM injection scoped to the grid's host:
    // listen for tool-panel open/close + watch the host subtree for
    // any rebuild, then re-inject Material Icon spans into matching
    // `.ag-column-select-column-label` elements.
    this.gridApi.addEventListener('toolPanelVisibleChanged', () => {
      this.injectColumnSelectorIcons();
    });
    this.setupColumnSelectorObserver();
  }

  /**
   * AG-Grid `columnVisible` event. Fires when the user toggles a column's
   * visibility via the columns tool panel (or a programmatic
   * `columnApi.setColumnVisible(...)` call). We respond by re-fetching
   * loaded blocks so the new `selectFields` set takes effect immediately
   * — without this, hiding a column would leave its data sitting in
   * memory (waste, invisible) and showing a previously-hidden column
   * would render empty cells until the next paginate.
   *
   * Uses `refreshLoadedBlocks` (purge: false) — we only need to update
   * the data, not reset pagination state.
   */
  public onColumnVisible(): void {
    this.gridApi?.refreshServerSide({ purge: false });
    this.persistColumnState();
  }

  /**
   * AG-Grid `columnMoved` event. In v28 this fires once each time a dragged
   * column changes index (per column crossed, not per pixel), so persisting
   * on every emit is cheap — every emit is also a valid layout snapshot.
   * Reordering has no data-fetch consequence, so there's nothing to refresh.
   */
  public onColumnMoved(): void {
    this.persistColumnState();
  }

  /**
   * AG-Grid `columnResized` event. Fires continuously while the user drags a
   * column border; the `finished` flag is true only on the settled width. We
   * persist on `finished` to capture the final width without thrashing
   * localStorage mid-drag. Flex / auto-size resizes also set `finished`, so
   * those persist too — desirable, they're real layout changes.
   */
  public onColumnResized(event: ColumnResizedEvent): void {
    if (!event.finished) return;
    this.persistColumnState();
  }

  /**
   * AG-Grid `sortChanged` event. Fires when the user clicks a column header
   * to sort or when sort is applied programmatically. Persist the new sort
   * state so it survives navigation within the app (cleared on page refresh,
   * login, and logout via the clearPersistedGridSort() utility, which strips
   * only the sort fields and leaves the column layout persisted).
   */
  public onSortChanged(): void {
    this.persistColumnState();
  }

  /**
   * AG-Grid `newColumnsLoaded` event — fires last in the column-rebuild
   * sequence after a `columnDefs` reassignment. When a re-apply is pending
   * (a post-ready runtime swap, flagged in `ngOnChanges('columnDefs')`),
   * re-apply the user's saved layout that the rebuild just discarded, then
   * lift the persistence suppression. No-op on the initial column load: the
   * flag is only set for post-ready reassignments, and the first restore is
   * done in `onGridReady`.
   */
  public onNewColumnsLoaded(): void {
    if (!this.columnDefsReapplyPending) return;
    this.columnDefsReapplyPending = false;
    this.restoreColumnState();
  }

  /**
   * Serialize the current column layout (order + visibility + width) and
   * hand it to the optional persistence service, keyed by `componentId`.
   * No-op when no service is provided, before the grid is ready, or while a
   * restore is in flight (the latter prevents the apply→event→save loop).
   */
  private persistColumnState(): void {
    if (this.isRestoringColumnState || this.columnDefsReapplyPending) return;
    if (!(this.saveStateService && this.columnApi)) return;
    const columnState = toPersistedColumnState(this.columnApi.getColumnState());
    // Persist pageSize alongside columnState in the SAME blob so a column
    // event never clobbers the stored page size (and vice-versa). Only carry
    // pageSize when the selector is active — grids without it shouldn't start
    // writing a page-size key.
    const state: PersistedGridState = { columnState };
    if (this.showPageSizeSelector) state.pageSize = this.currentPageSize;
    this.saveStateService.saveComponentState(this.componentId, state);
  }

  /**
   * Read the persisted column layout (if any) and apply it. `applyOrder:
   * true` reorders columns to match the saved sequence; `hide` + `width`
   * ride along on each entry. Columns absent from the saved layout (e.g.
   * added to `columnDefs` since the layout was saved) are left at their
   * default position/visibility. A missing or malformed blob is ignored.
   *
   * No `defaultState` here, deliberately. AG-Grid's `applyColumnState` falls
   * back to `defaultState` per attribute whenever the matched state entry
   * omits it, so a `defaultState: { sort: null }` clears the default sort
   * declared in `columnDefs` — which is exactly what must NOT happen on the
   * refresh restore, where `clearPersistedGridSort()` has just dropped the
   * `sort`/`sortIndex` fields from the saved entries so the grid falls back to
   * that default. The navigation restore still applies the user's sort
   * faithfully: `getColumnState()` emits an explicit `sort` (null when
   * unsorted) for every column, so those blobs carry their own clearing.
   */
  private restoreColumnState(): void {
    if (!(this.saveStateService && this.columnApi)) return;
    const saved = this.saveStateService.retrieveComponentState(
      this.componentId
    );
    const columnState = readPersistedColumnState(saved);
    if (!columnState || columnState.length === 0) return;
    this.isRestoringColumnState = true;
    try {
      this.columnApi.applyColumnState({
        state: columnState,
        applyOrder: true,
      });
    } finally {
      this.isRestoringColumnState = false;
    }
  }

  /**
   * Read the persisted page size (validated against the current option list)
   * for seeding `currentPageSize` in `ngOnInit`. Returns null when there's no
   * service, no valid stored value, or the selector is disabled — the caller
   * then falls back to the `paginationPageSize` @Input.
   */
  private readRestoredPageSize(): number | null {
    if (!(this.showPageSizeSelector && this.saveStateService)) return null;
    const saved = this.saveStateService.retrieveComponentState(this.componentId);
    return readPersistedPageSize(saved, this.pageSizeOptions);
  }

  /**
   * User picked a new page size in the footer selector (EDUS-2713). Apply it
   * to both pagination and the SSRM block size, reset to page 1, re-fetch,
   * and persist. `gridOptions.cacheBlockSize` is mutated on the SAME object
   * AG-Grid holds — `gridOptionsWrapper.getCacheBlockSize()` reads it live.
   *
   * EDUS-2713 QA bug: an earlier version of this method called
   * `gridApi.refreshServerSide({ purge: true })` here instead of
   * `setServerSideDatasource`. On a large dataset (~10k+ rows) that made the
   * pagination summary get stuck on "1 to ? of N" permanently — reproduced
   * and root-caused via a temporary diagnostic Storybook story plus patched
   * AG-Grid internals (see PR discussion): `refreshServerSide({purge:true})`
   * against a big row count / block-size delta races AG-Grid 28.2.1's
   * `ServerSideRowModel` into destroying an in-flight block while its
   * `RowNodeBlockLoader` still counts it as active, and — unlike the case
   * AG-Grid's own source comments describe — the orphaned block's
   * `EVENT_LOAD_COMPLETE` never fires, so `activeBlockLoadsCount` never
   * drops back to 0 and the paginator shows '?' forever
   * (`PaginationComp.updateRowLabels` gates on `rowNodeBlockLoader.isLoading()`).
   * `setServerSideDatasource(...)` fully re-creates the store instead of
   * purging the existing one in place, which sidesteps the race entirely.
   * Verified against a 10,070-row / 500-page-size repro, including repeated
   * page-size switches and page navigation after the switch.
   */
  public onPageSizeChange(pageSize: number): void {
    if (pageSize === this.currentPageSize) return;
    this.currentPageSize = pageSize;
    if (!this.gridApi) return;
    this.gridOptions.cacheBlockSize = resolveCacheBlockSize(pageSize, this.cacheBlockSize);
    this.gridApi.paginationSetPageSize(pageSize);
    this.gridApi.paginationGoToPage(0);
    this.gridApi.setServerSideDatasource(this.sqlSsrmDatasource);
    this.persistColumnState();
  }

  /**
   * EDUS-2713: relocate the footer's "Rows Per Page" control into AG-Grid's
   * native `.ag-paging-panel` (a `justify-content: flex-end` flex row) so it
   * lays out immediately left of the "x to y of N" summary with a fixed
   * `margin-right` gap (see SCSS). This is robust where a fixed overlay
   * offset is not: the summary widens with the row count (e.g. "of 6,782"),
   * and an absolutely-positioned overlay would overlap it at large totals.
   * The node keeps its Angular view-encapsulation attribute and event
   * bindings across the re-parent, so `(change)` and change-detection still
   * work. Idempotent — no-op once the control already lives in the panel.
   */
  private mountPageSizeSelectorInPaginator(): void {
    if (!this.showPageSizeSelector) return;
    const host = this.elementRef.nativeElement;
    const panel = host.querySelector<HTMLElement>('.ag-paging-panel');
    const control = host.querySelector<HTMLElement>('.page-size-container');
    if (!control) return;
    if (!panel) {
      // Signal the failure instead of silently leaving the control in its
      // default overlay position — if a future AG-Grid version renames or
      // restructures `.ag-paging-panel`, this fires once instead of leaving
      // a layout regression undiagnosed. Warned once per instance since
      // this method also runs on every `paginationChanged` event.
      if (!this.pageSizeSelectorMountWarned) {
        this.pageSizeSelectorMountWarned = true;
        // biome-ignore lint/suspicious/noConsole: developer-facing diagnostic for an AG-Grid internal DOM structure this relies on changing underneath us — silent failure here would make a future layout regression much harder to diagnose
        console.warn(
          '[CdwSsrmGridComponent] Could not find AG-Grid ".ag-paging-panel" to ' +
            'relocate the page-size selector; it remains in its default overlay ' +
            'position. This likely means the installed AG-Grid version changed ' +
            'its paginator DOM structure (last verified against AG-Grid 28.2.1).'
        );
      }
      return;
    }
    if (control.parentElement === panel) return;
    panel.insertBefore(control, panel.firstChild);
  }

  /**
   * Compute the `selectFields` list to send on the next block fetch.
   * Union of:
   *   - currently-visible non-virtual columns (those whose colId matches
   *     a `field` in the consumer-supplied `columnDefs`), and
   *   - the consumer-supplied `alwaysRequiredFields` list.
   *
   * Returns an empty array when neither input has anything to contribute,
   * which the factory treats as "don't send selectFields, let the backend
   * default to full entities" (the legacy behavior for consumers that
   * haven't opted in to wire-format reduction).
   *
   * Excludes virtual columns (preview icon, select checkbox) — those
   * have a `colId` but no `field`, so they don't map to row-data paths
   * and the backend would reject them anyway.
   */
  private computeSelectFields(): string[] {
    if (!this.columnApi) return [...this.alwaysRequiredFields];
    // Build a set of valid field-bearing colIds from the current
    // columnDefs. AG-Grid uses colId if explicitly set, else the field name.
    const fieldColIds = new Set<string>();
    for (const colDef of this.columnDefs) {
      if (!colDef.field) continue;
      fieldColIds.add(colDef.colId ?? colDef.field);
    }
    const visibleFields = this.columnApi
      .getColumnState()
      .filter((state) => !state.hide && fieldColIds.has(state.colId))
      .map((state) => state.colId);
    return Array.from(
      new Set([...visibleFields, ...this.alwaysRequiredFields])
    );
  }

  /**
   * AG-Grid `modelUpdated` event. Fires after any model change including
   * SSRM block loads. Used here as the trigger for mirroring server-
   * provided per-row selection state (`node.data.isSelected`, populated
   * by Phase 3's enrichment) onto AG-Grid's `node.isSelected()` so the
   * checkbox column visually reflects the authoritative state.
   *
   * Sort/filter/pagination also fire `modelUpdated`, so this handler is
   * the single point of truth for "make visible row checkboxes match the
   * server's selection truth". Cheap when nothing changed (early-exits
   * on already-correct nodes).
   */
  public onModelUpdated(): void {
    this.mirrorIsSelectedFromData();
    this.recomputeSelectedOnPageCount();
  }

  /**
   * AG-Grid `paginationChanged` event handler. Recompute the per-page
   * selection count whenever the user pages forward/back so the footer's
   * "(across pages)" hint stays accurate. Listed separately from
   * modelUpdated because AG-Grid can fire pagination changes without a
   * model update (e.g., page size change before block load).
   */
  public onPaginationChanged(): void {
    this.mountPageSizeSelectorInPaginator();
    this.recomputeSelectedOnPageCount();
    // EDUS-2505: paginating signals the user is curating selection
    // manually rather than opting into a cross-page select-all. Hide
    // the page-prompt banner. The all-matching banner is unaffected —
    // its visibility is derived purely from `selectedCount === itemTotal`,
    // not from `showPagePrompt$` — so the "Clear selection" state
    // correctly persists across pagination.
    this.promptService.dismissPrompt();
  }

  /**
   * Walk visible rows on the current page and emit the count of those
   * with `node.isSelected() === true`. Used by the footer to detect
   * cross-page selection: if the global count exceeds this number, the
   * footer renders an "(across pages)" disclosure so the user knows
   * "Clear selection" is global before they click it.
   *
   * Identical visible-rows walk to SsrmHeaderCheckboxComponent — kept
   * separate because the header is an AG-Grid component without access
   * to this BehaviorSubject, and a service-mediated split would be more
   * machinery than a 10-line dup.
   */
  private recomputeSelectedOnPageCount(): void {
    if (!this.gridApi) return;
    const currentPage = this.gridApi.paginationGetCurrentPage();
    const pageSize = this.gridApi.paginationGetPageSize();
    const totalRows = this.gridApi.paginationGetRowCount();
    const start = currentPage * pageSize;
    const endExclusive = Math.min(start + pageSize, totalRows);
    let count = 0;
    for (let i = start; i < endExclusive; i++) {
      const node = this.gridApi.getDisplayedRowAtIndex(i);
      if (node?.isSelected()) count++;
    }
    if (this.selectedOnPageCountSubject.value !== count) {
      this.selectedOnPageCountSubject.next(count);
    }
  }

  /**
   * AG-Grid `selectionChanged` event. Fires for USER-driven selection
   * changes — individual row clicks AND the header-checkbox batch toggle.
   * Programmatic mirror calls from `mirrorIsSelectedFromData` use
   * `suppressFinishActions=true` so they don't re-enter here.
   *
   * Phase 7 of EDUS-2485 changed semantics: instead of computing the full
   * cross-page Set + emitting `(selectionChanged)`, we diff each visible
   * node's `node.isSelected()` against its `node.data.isSelected` (the
   * server-provided truth) and emit one `(selectionToggled)` per changed
   * row. Optimistically update `node.data.isSelected` on the row data so
   * the visual stays consistent until the consumer's batched callback
   * persists the change to the server.
   *
   * For batch operations (header checkbox click → N visible rows toggled)
   * AG-Grid fires this once at the end of the batch — we emit N events.
   * The consumer's debounce (Phase 9) collapses the burst into a single
   * `selectByIds` / `deselectByIds` server call.
   */
  public onSelectionChanged(): void {
    if (!this.gridApi) return;
    if (this.currentLockActive) {
      // Selection lock in flight. CSS `pointer-events: none` on the
      // `.cdw-ssrm-grid--selection-locked` host class blocks mouse
      // clicks on the checkbox column, but keyboard interaction (Space
      // on a focused checkbox) and programmatic `setSelected` calls
      // can still trip this handler. Roll any visual change back to
      // `node.data.isSelected` (the server truth) silently — no
      // selectionToggled emit, no debounced batch sync to the server.
      // The lock will release on jobCompleted, after which normal
      // interaction resumes.
      // Per-node revert mechanics extracted into `selection-lock-revert.ts`
      // so they can be unit-tested without importing the full component
      // (see that file's header for the AG-Grid + vitest rationale).
      // Key contract: if `data.isSelected` is undefined we leave the node's
      // AG-Grid state alone — force-deselecting would silently destroy any
      // selection the user made before the lock activated.
      this.gridApi.forEachNode((node) =>
        revertNodeSelectionToServerTruth(node)
      );
      return;
    }
    this.gridApi.forEachNode((node) => {
      if (!node.data) return;
      const id = String(node.data.id);
      const isSelectedNow = node.isSelected();
      const data = node.data as { isSelected?: boolean };
      const wasSelected = data.isSelected === true;
      if (isSelectedNow === wasSelected) return;
      // Optimistically update the row data so the next mirror cycle
      // sees no diff (prevents re-emit on the next modelUpdated).
      data.isSelected = isSelectedNow;
      this.selectionToggled.emit({
        rowId: id,
        op: isSelectedNow ? 'select' : 'deselect',
      });
    });
    this.recomputeSelectedOnPageCount();
    // EDUS-2505: any user-driven selection change dismisses the page-prompt
    // banner. Two important nuances:
    //
    // 1. INITIAL HEADER-CHECKBOX CLICK: the header click is supposed to
    //    TRIGGER the prompt, not dismiss it. Ordering saves us — AG-Grid
    //    fires `selectionChanged` synchronously (suppressAsyncEvents:true)
    //    from the last `setSelected` call in SsrmHeaderCheckboxComponent.onClick,
    //    which runs BEFORE the same method's subsequent
    //    `promptService.triggerPagePrompt()` call. So: dismiss → trigger →
    //    prompt visible. The dismiss is a no-op transitional state.
    //
    // 2. SELECT-ALL-MATCHING DOESN'T DISMISS: the cross-page select-all
    //    flow (`selectAllMatching()` → `selectByFilter` server call →
    //    `refreshServerSide({purge:true})`) updates selection via mirror
    //    calls that use `suppressFinishActions=true` — those DON'T fire
    //    `selectionChanged`. The all-matching banner appears when the
    //    consumer's `selectedCount$` reaches `itemTotal$`, driven by
    //    server state — no path through this handler.
    //
    // 3. ALL-MATCHING DESELECT: when the user deselects from all-matching
    //    mode, we want the banner to hide entirely (not fall back to
    //    page-prompt). Dismissing `showPagePrompt$` here ensures that
    //    once `selectedCount` drops below `itemTotal` the banner has no
    //    visibility path left.
    this.promptService.dismissPrompt();
  }

  /**
   * Imperative "select all matching rows" — invokes the consumer's
   * `[selectByFilter]` callback with the current filter bar selections.
   * The consumer's adapter forwards to the server-side selection store,
   * which resolves the filter to ids and adds them to the user's Redis
   * set. On success, the grid triggers `refreshServerSide({purge:true})`
   * so the next page-fetch returns rows with `isSelected:true` for the
   * newly-selected ids (and the per-row mirror handles the visuals).
   *
   * No-op + console warning when `selectByFilter` is not wired.
   */
  public selectAllMatching(): void {
    if (!this.selectByFilter) {
      // biome-ignore lint/suspicious/noConsole: developer-facing diagnostic for misconfiguration of an optional @Input — silent failure here would make the missing wiring much harder to diagnose
      console.warn(
        'CdwSsrmGridComponent.selectAllMatching: [selectByFilter] @Input is not wired. ' +
          'Pass a callback returning Observable<{count: number}> to enable cross-page select-all.'
      );
      return;
    }
    this.promptService.setSelectAllInProgress(true);
    this.selectByFilter(this.currentFilters)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.promptService.setSelectAllInProgress(false))
      )
      .subscribe({
        next: () => {
          // Re-fetch with selection applied so per-row isSelected propagates.
          this.gridApi?.refreshServerSide({ purge: true });
        },
        error: (error) => {
          this.errorRetrievingData.emit(
            error instanceof Error ? error : new Error(String(error))
          );
        },
      });
  }

  /**
   * Imperative "deselect all matching rows" — mirror of `selectAllMatching`
   * for the deselect-all-matching path. Invokes `[deselectByFilter]` with
   * the current filter, then refreshes. Used by the banner's "Clear
   * selection" link when the consumer has wired both callbacks.
   */
  public deselectAllMatching(): void {
    if (!this.deselectByFilter) {
      // biome-ignore lint/suspicious/noConsole: developer-facing diagnostic
      console.warn(
        'CdwSsrmGridComponent.deselectAllMatching: [deselectByFilter] @Input is not wired.'
      );
      return;
    }
    this.deselectByFilter(this.currentFilters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.gridApi?.refreshServerSide({ purge: true }),
        error: (error) =>
          this.errorRetrievingData.emit(
            error instanceof Error ? error : new Error(String(error))
          ),
      });
  }

  /**
   * Imperative "clear all selections" — invokes the consumer's
   * `[clearAllSelected]` callback to drop the entire user selection set
   * on the server, then triggers `refreshServerSide({purge:true})` so
   * per-row `isSelected:false` propagates and visible checkboxes clear.
   *
   * No-op + console warning when `clearAllSelected` is not wired.
   */
  public clearSelection(): void {
    if (!this.clearAllSelected) {
      // biome-ignore lint/suspicious/noConsole: developer-facing diagnostic
      console.warn(
        'CdwSsrmGridComponent.clearSelection: [clearAllSelected] @Input is not wired.'
      );
      return;
    }
    this.clearAllSelected()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.gridApi?.refreshServerSide({ purge: true }),
        error: (error) =>
          this.errorRetrievingData.emit(
            error instanceof Error ? error : new Error(String(error))
          ),
      });
  }

  /**
   * Walk currently-rendered nodes and call `setSelected(...)` to mirror
   * server-provided `node.data.isSelected` onto AG-Grid's
   * `node.isSelected()`. Uses the silent variant
   * (`suppressFinishActions = true`) so this sync doesn't fire AG-Grid's
   * `selectionChanged` event (which would re-enter `onSelectionChanged`
   * and emit no-op `(selectionToggled)` events).
   *
   * Authority: `node.data.isSelected` is the server's truth (Phase 3
   * enrichment populates it on every page-fetch). AG-Grid's
   * `node.isSelected()` is the visual layer that drives the checkbox.
   * This mirror keeps the visual in sync with the server after every
   * block load.
   */
  private mirrorIsSelectedFromData(): void {
    if (!this.gridApi) return;
    let didMutate = false;
    this.gridApi.forEachNode((node) => {
      if (!node.data) return;
      const shouldBeSelected =
        (node.data as { isSelected?: boolean }).isSelected === true;
      if (node.isSelected() !== shouldBeSelected) {
        node.setSelected(shouldBeSelected, false, true);
        didMutate = true;
      }
    });
    // Refresh the header checkbox since its `checked`/`indeterminate`
    // state derives from visible nodes' `isSelected()` and the silent
    // setSelected calls above don't fire its event listeners.
    if (didMutate) {
      this.gridApi.refreshHeader();
    }
  }

  /**
   * Handle filter bar changes. Three things happen, in order:
   *   1. Update internal `currentFilters` (the SSRM datasource's source of truth).
   *   2. Emit `(filtersChange)` to the consumer (typically mirrored to NgRx
   *      for URL persistence).
   *   3. Tell AG-Grid to invalidate all cached blocks and re-fetch from page 0.
   *
   * Order matters: state update FIRST so that if the consumer's handler
   * re-reads our state synchronously, it sees the new filters. The
   * `refreshServerSide({purge: true})` call triggers a `getRows` callback
   * which reads `this.currentFilters`.
   */
  public onFilterBarChange(filterFields: SelectedFilterField[]): void {
    const prevFilters = this.currentFilters;
    this.currentFilters = filterFields;
    // Page-prompt banner is filter-state-dependent — always reset on any
    // filter change regardless of selection-preservation decision.
    this.promptService.clear();
    // EDUS-2528 follow-up: only clear cross-page selection when the new
    // filter is at least as restrictive as the previous one (e.g., a
    // line was added, a value was narrowed, a matcher swapped). When the
    // filter is REMOVED or otherwise made more permissive, the
    // previously-selected rows are still in scope and clearing destroys
    // the user's work. `isFilterChangePermissive` is a conservative
    // structural-subset check — see its JSDoc for the false-negative
    // trade-off.
    const permissive = isFilterChangePermissive(prevFilters, filterFields);
    if (!permissive && this.clearAllSelected) {
      this.clearAllSelected()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          // Server clear succeeded; the refresh below will surface
          // isSelected:false on every row.
          error: (error) =>
            this.errorRetrievingData.emit(
              error instanceof Error ? error : new Error(String(error))
            ),
        });
    }
    this.filtersChange.emit({
      filters: filterFields,
      filterBarId: this.filterBarId,
    });
    this.gridApi?.refreshServerSide({ purge: true });
  }

  /**
   * Banner-emitted dismiss handler. Hides the page-prompt without clearing
   * the underlying selection (so user can dismiss the prompt and keep
   * their 50 rows selected; banner won't reappear until next header-checkbox
   * click or filter change).
   */
  public onSelectAllBannerDismiss(): void {
    this.promptService.dismissPrompt();
  }

  /**
   * Banner-emitted "Clear selection" handler. Both clears the selection
   * (round-trips through consumer state service) AND resets prompt state.
   */
  public onSelectAllBannerClear(): void {
    this.clearSelection();
    this.promptService.clear();
  }

  public onRowClicked(event: RowClickedEvent): void {
    this.rowClicked.emit(event);
  }

  /**
   * AG-Grid `cellClicked` handler. Re-emits the event with the column
   * identity intact so consumers can route navigation off a specific
   * column (e.g., an eye-icon view column) without entangling it with
   * generic row-click behavior.
   *
   * EDUS-2508: When [showNonEditableTooltips] is true, also surface a
   * "Permission restricted. View only." tooltip on clicks landing on
   * non-editable cells. Runs BEFORE the consumer's handler so the
   * consumer's own routing (e.g., View-icon navigation) remains independent
   * — the tooltip appears regardless of whether the consumer handles the
   * click. Using `Column.isCellEditable(node)` to let AG-Grid evaluate the
   * (possibly-wrapped) editable predicate against the row, rather than
   * reconstructing EditableCallbackParams ourselves.
   */
  public onCellClicked(event: CellClickedEvent): void {
    if (
      this.showNonEditableTooltips &&
      event.column &&
      !event.column.isCellEditable(event.node)
    ) {
      this.showClickTooltip(
        event.event as MouseEvent,
        'Permission restricted. View only.'
      );
    }
    this.cellClicked.emit(event);
  }

  /**
   * Append a transient tooltip to document.body positioned near the click.
   * Tracks cursor via mousemove and self-removes on mouseleave or on the
   * next call (single-instance). Ported behavior-for-behavior from the
   * legacy `cdw-search-grid` `showClickTooltip` (EDUS-2508) so users see
   * identical feedback across both grid implementations.
   *
   * Styling uses AG-Grid's `.ag-tooltip` class for visual consistency with
   * native AG-Grid tooltips; the inline styles below are defensive defaults
   * in case the host theme doesn't load the alpine tooltip styles.
   */
  private showClickTooltip(mouseEvent: MouseEvent, message: string): void {
    this.removeClickTooltip();

    const cellElement = (mouseEvent.target as HTMLElement)?.closest('.ag-cell');
    if (!cellElement) return;

    const tooltip = document.createElement('div');
    tooltip.className = 'cdw-click-tooltip ag-tooltip';
    tooltip.textContent = message;
    tooltip.style.position = 'fixed';
    tooltip.style.backgroundColor = '#f5f5f5';
    tooltip.style.color = '#000';
    tooltip.style.padding = '5px';
    tooltip.style.border = '1px solid #ddd';
    tooltip.style.borderRadius = '2px';
    tooltip.style.fontSize = '12px';
    tooltip.style.fontFamily = 'inherit';
    tooltip.style.zIndex = '10000';
    tooltip.style.pointerEvents = 'none';
    tooltip.style.whiteSpace = 'nowrap';
    tooltip.style.boxShadow = '0 1px 4px 1px rgba(0, 0, 0, 0.2)';
    tooltip.style.transition = 'opacity 0.2s';

    this.positionTooltip(tooltip, mouseEvent.clientX, mouseEvent.clientY);
    document.body.appendChild(tooltip);
    this.activeClickTooltip = tooltip;

    // Track cursor and dismiss on mouseleave. Handlers are captured as
    // class fields so removeClickTooltip can pass the SAME reference to
    // removeEventListener (anonymous arrow function would leak).
    this.tooltipMouseMoveHandler = (e: Event) => {
      const me = e as MouseEvent;
      if (this.activeClickTooltip) {
        this.positionTooltip(this.activeClickTooltip, me.clientX, me.clientY);
      }
    };
    this.tooltipMouseLeaveHandler = () => this.removeClickTooltip();
    cellElement.addEventListener('mousemove', this.tooltipMouseMoveHandler);
    cellElement.addEventListener('mouseleave', this.tooltipMouseLeaveHandler);
  }

  /** Position tooltip 10px right + 10px below the cursor. */
  private positionTooltip(
    tooltip: HTMLElement,
    mouseX: number,
    mouseY: number
  ): void {
    tooltip.style.left = `${mouseX + 10}px`;
    tooltip.style.top = `${mouseY + 10}px`;
  }

  /**
   * Remove the active tooltip element + its mousemove/mouseleave listeners.
   * Safe to call when nothing is active. Iterates `.ag-cell` to symmetrically
   * remove the listeners regardless of which cell currently holds them —
   * cells get re-rendered on scroll/sort so we can't rely on the same node
   * still being the original target.
   */
  private removeClickTooltip(): void {
    if (this.activeClickTooltip) {
      this.activeClickTooltip.remove();
      this.activeClickTooltip = null;
    }
    if (!(this.tooltipMouseMoveHandler || this.tooltipMouseLeaveHandler)) return;
    const cells = this.elementRef.nativeElement.querySelectorAll('.ag-cell');
    cells.forEach((cell: Element) => {
      if (this.tooltipMouseMoveHandler) {
        cell.removeEventListener('mousemove', this.tooltipMouseMoveHandler);
      }
      if (this.tooltipMouseLeaveHandler) {
        cell.removeEventListener('mouseleave', this.tooltipMouseLeaveHandler);
      }
    });
    this.tooltipMouseMoveHandler = null;
    this.tooltipMouseLeaveHandler = null;
  }

  /**
   * AG-Grid fires `firstDataRendered` once after the first block of rows is
   * displayed. Use this hook to restore initial-page state (consumer-supplied
   * via `[initialPage]`) and to emit our `firstDataRendered` event for the
   * consumer to gate any "wait until grid has data" workflows on (e.g. the
   * Wipe Profiles auto-trigger).
   */
  public onFirstDataRendered(): void {
    if (this.hasEmittedFirstDataRendered) return;
    this.hasEmittedFirstDataRendered = true;
    if (this.initialPage != null && this.initialPage > 0) {
      this.gridApi?.paginationGoToPage(this.initialPage);
    }
    this.mountPageSizeSelectorInPaginator();
    this.firstDataRendered.emit();
  }

  // === Public imperative API ===
  // The consumer (`DeviceSearchComponent`) needs imperative access to a few
  // grid operations: surgical row updates after a job finishes, full-block
  // refresh after a backend cache invalidation, and lightweight queries on
  // current pagination state. These methods are thin wrappers around the
  // AG-Grid `gridApi` with null-safety for the pre-`onGridReady` lifecycle
  // window. Validated via Storybook (the `WithImperativeApi` story); not
  // unit-tested directly because importing the component class triggers
  // the AG-Grid v28 UMD/ESM interop failure that excludes our component
  // specs from vitest (see `vite.config.mts`).

  /**
   * Surgically update specific rows in loaded SSRM blocks via AG-Grid's
   * server-side transaction API. Rows whose `id` doesn't currently match a
   * loaded row node are silently skipped — they'll fetch fresh from the
   * backend when the user next paginates to them. Use this after a job
   * completes to reflect mutations without re-fetching the whole block.
   *
   * Empty input is a no-op (saves an unnecessary AG-Grid call).
   */
  public applyRowUpdates(rows: T[]): void {
    if (!this.gridApi || rows.length === 0) return;
    this.gridApi.applyServerSideTransaction({ update: rows });
    // AG-Grid v28's applyServerSideTransaction (partial store) updates
    // node.data but doesn't reliably trigger cell re-render — especially
    // for nested object paths like vendorProperties.google.orgUnitPath
    // whose changes the internal value-diff misses. force: true bypasses
    // the diff. Scoped to the updated nodes via getRowNode lookup so we
    // don't touch unaffected rows.
    const updatedNodes = rows
      .map((row) => this.gridApi?.getRowNode(String(row.id)))
      .filter((node): node is NonNullable<typeof node> => node != null);
    if (updatedNodes.length > 0) {
      this.gridApi.refreshCells({ rowNodes: updatedNodes, force: true });
    }
  }

  /**
   * Re-fetch all currently loaded blocks WITHOUT purging the cache (existing
   * blocks stay loaded; AG-Grid refetches their data). Use after a backend
   * cache invalidation event when the row IDENTITIES haven't changed but
   * the row data may have.
   */
  public refreshLoadedBlocks(): void {
    this.gridApi?.refreshServerSide({ purge: false });
  }

  /**
   * Purge all cached blocks and re-fetch from block 0. Use when the
   * underlying row set has changed enough that pagination state from the
   * old result set isn't meaningful (e.g. a major filter change applied
   * imperatively rather than through the embedded filter bar).
   *
   * Note: the embedded filter bar (`onFilterBarChange`) already does this
   * automatically. This method is for consumers that mutate filter state
   * via a non-filter-bar path (URL navigation, programmatic filter
   * presets, etc.).
   */
  public purgeAndRefresh(): void {
    this.gridApi?.refreshServerSide({ purge: true });
  }

  /**
   * Alias for `purgeAndRefresh()`. Preserves the legacy `CdwSearchGridComponent`
   * `refreshGrid()` call surface so consumers that recover state across page
   * loads (e.g. `recoverPendingForceRefreshJobs` in `DeviceSearchComponent`,
   * keep their existing call sites unchanged.
   */
  public refreshGrid(): void {
    this.purgeAndRefresh();
  }

  /** Current 0-indexed page number, or 0 if the grid hasn't initialized. */
  public getCurrentPage(): number {
    return this.gridApi?.paginationGetCurrentPage() ?? 0;
  }

  /**
   * Total row count across all pages, as reported by the backend in the
   * most recent `fetchSqlData` response. 0 if the grid hasn't initialized
   * or no data has been fetched yet.
   */
  public getItemTotal(): number {
    return this.gridApi?.paginationGetRowCount() ?? 0;
  }

  /**
   * IDs (as strings, matching `getRowId`) of rows currently held by AG-Grid
   * — i.e. rows in the loaded blocks that haven't been evicted. Useful for
   * consumers that need to side-fetch supplementary data only for visible
   * rows (e.g. the audit-log enrichment pattern). Returns an
   * empty array before the first block load.
   */
  public getRenderedRowIds(): string[] {
    const ids: string[] = [];
    this.gridApi?.forEachNode((node) => {
      if (node.data?.id != null) ids.push(String(node.data.id));
    });
    return ids;
  }

  /**
   * Look up row data by id from AG-Grid's loaded blocks. Returns
   * `undefined` for rows currently evicted (or before the first block
   * load). Companion to `getRenderedRowIds()` — consumers can split a
   * known id list into "have data" vs "need side-fetch" sets without
   * reaching into the protected `gridApi`.
   */
  public getRowDataById(id: string): T | undefined {
    return this.gridApi?.getRowNode(id)?.data;
  }

  /**
   * Current column order with visibility flags. Reads `columnApi.getColumnState()`
   * so it reflects user reorders / column-tool-panel hides. Returns an empty
   * array before the grid initializes. Consumers that build export/import
   * dialog payloads need this to round-trip the user's column layout.
   */
  public getColumnOrder(): CdwGridColumn[] {
    const columnState = this.columnApi?.getColumnState() ?? [];
    return columnState.map((col) => ({
      name: col.colId,
      hide: col.hide ?? false,
    }));
  }

  /**
   * Current sort order in priority order. Filters to sorted columns only
   * and uppercases AG-Grid's lowercase 'asc'/'desc' to match the
   * `CdwGridSort.direction` contract.
   */
  public getSortOrder(): CdwGridSort[] {
    const columnState = this.columnApi?.getColumnState() ?? [];
    return columnState
      .filter((col) => col.sort)
      .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
      .map((col) => ({
        name: col.colId,
        direction: col.sort?.toUpperCase() as 'ASC' | 'DESC',
      }));
  }

  // === In-cell edit imperative API + helpers ===

  /**
   * Commit (and close) any cell editor that's currently open. No-op when
   * no editor is active. Use BEFORE triggering a refresh that would clobber
   * the editor state (e.g., the consumer's "show changes only" toggle
   * which fires `refreshServerSide({purge: true})` and would otherwise
   * leave a half-open editor visual behind).
   *
   * Distinct from `exitEditMode()` — does NOT cancel the value, does NOT
   * clear `modifiedCellsByRow`, does NOT trigger `refreshCells`. Pure
   * "commit any open editor" so the user's typed value lands in
   * `node.data` (and fires `cellValueChanged` if the value changed).
   */
  public commitOpenEditor(): void {
    this.gridApi?.stopEditing(false);
  }

  /**
   * Cleanup signal called by the consumer after Save success or Cancel
   * confirm: cancels any open cell editor, clears the modified-cell
   * tracking, and force-refreshes cells so `.modified-cell` visuals drop
   * immediately. The consumer's cellChanges Map is consumer-owned and
   * cleared separately (see device-search consumer's clearCellChanges).
   */
  public exitEditMode(): void {
    this.gridApi?.stopEditing(true);
    this.modifiedCellsByRow.clear();
    this.gridApi?.refreshCells({ force: true });
  }

  /**
   * Wrap AG-Grid's `cellEditingStarted`: snapshot the original value and
   * install a host-level `input` listener so we can compute "cell
   * dirty" before the user commits. Then re-emit so existing consumer
   * subscribers (e.g., consumer's `isEditingActive` flag) keep working.
   *
   * Single shared `input` listener on the grid host catches every
   * keystroke in the active cell editor's `<input>` (events bubble
   * through the cell DOM). On each fire we read the live editor value
   * via `gridApi.getCellEditorInstances()` and compare to the snapshot.
   */
  protected handleCellEditingStarted(event: CellEditingStartedEvent): void {
    this.editingOriginalValue = event.value;
    this.isEditingDirty = false;
    // Listener takes no params on each fire — it reads current state via
    // the grid api. Stored as a class field so removeEventListener can
    // pass the same reference back in handleCellEditingStopped.
    this.editorInputListener = () => this.recomputeEditingDirty();
    this.elementRef.nativeElement.addEventListener(
      'input',
      this.editorInputListener
    );
    this.cellEditingStarted.emit(event);
  }

  /**
   * Wrap AG-Grid's `cellEditingStopped`: tear down the input listener
   * and reset dirty-tracking state so the consumer's
   * `cellEditingDirtyChanged` subscriber drops back to `false`. Whether
   * the editor closed via commit (Enter/Tab/blur) or cancel (Esc) is
   * irrelevant here — either way no editor is open after this fires.
   *
   * Re-emit `cellEditingStopped` after cleanup so consumer-side handlers
   * see a coherent post-edit state (no stale dirty signal).
   */
  protected handleCellEditingStopped(event: CellEditingStoppedEvent): void {
    if (this.editorInputListener) {
      this.elementRef.nativeElement.removeEventListener(
        'input',
        this.editorInputListener
      );
      this.editorInputListener = undefined;
    }
    this.editingOriginalValue = undefined;
    if (this.isEditingDirty) {
      this.isEditingDirty = false;
      this.cellEditingDirtyChanged.emit(false);
    }
    this.cellEditingStopped.emit(event);
  }

  /**
   * Read the live editor value via `gridApi.getCellEditorInstances()`
   * and compare to `editingOriginalValue`. Emit `cellEditingDirtyChanged`
   * only on transition (not every keystroke) so consumer subscribers
   * don't see a flood of redundant emits.
   *
   * Loose-equality (`!=`) comparison so `null` and `undefined` collapse
   * (a cell with no value can be opened with `null` original, editor
   * may report empty-string current — both should read as "not dirty").
   * Strict equality would flicker dirty=true for that boundary case.
   */
  private recomputeEditingDirty(): void {
    if (!this.gridApi) return;
    const editors = this.gridApi.getCellEditorInstances();
    const editor = editors[0] as { getValue?: () => unknown } | undefined;
    if (!editor?.getValue) return;
    const currentValue = editor.getValue();
    // biome-ignore lint/suspicious/noDoubleEquals: intentional null/undefined coalescing — see method doc
    const dirty = currentValue != this.editingOriginalValue;
    if (dirty !== this.isEditingDirty) {
      this.isEditingDirty = dirty;
      this.cellEditingDirtyChanged.emit(dirty);
    }
  }

  /**
   * AG-Grid `cellValueChanged` event. Tracks which cell was modified
   * (drives the `.modified-cell` background color via the wrapped
   * `cellClass`) and re-emits the event so the consumer can update its
   * cellChanges Map.
   *
   * No-op when the new value equals the old value (AG-Grid sometimes fires
   * this event for blur-without-edit; the consumer would otherwise see a
   * spurious "modified" entry).
   *
   * Also no-op when `event.source === 'cdw-undo'` — that's the synthetic
   * source string we attach in `handleCellUndoClicked()` when reverting a
   * cell programmatically via `setDataValue(...)`. Without this guard the
   * undo would fire cellValueChanged → re-add the field to
   * `modifiedCellsByRow` → ghost-restore the `.modified-cell` visual AND
   * the consumer would re-add to its cellChanges Map (defeating the undo).
   */
  protected onCellValueChanged(event: CellValueChangedEvent): void {
    if (event.source === CDW_UNDO_EVENT_SOURCE) return;
    if (event.newValue === event.oldValue) return;
    const data = event.data as { id?: string | number } | undefined;
    const field = event.colDef.field;
    if (data?.id != null && field) {
      const rowId = String(data.id);
      let fields = this.modifiedCellsByRow.get(rowId);
      if (!fields) {
        fields = new Map();
        this.modifiedCellsByRow.set(rowId, fields);
      }
      // Set-if-absent: capture `event.oldValue` ONLY on the FIRST edit per
      // cell per edit session. A second edit to the same cell finds the
      // entry already present, so we preserve the genuine original (the
      // value as it stood before ANY edit this session) rather than
      // overwriting with the value-before-the-second-edit. The undo
      // overlay then reverts to that genuine original.
      if (!fields.has(field)) {
        fields.set(field, event.oldValue);
      }
      // AG-Grid doesn't auto-rerun cellClass on a value change unless the
      // cell re-renders, so the .modified-cell class would only get
      // applied on the NEXT interaction (clicking the cell again,
      // paginating, etc.). Force the cell to re-render now so the user
      // sees immediate visual feedback after committing an edit.
      this.gridApi?.refreshCells({
        rowNodes: [event.node],
        columns: [event.column],
        force: true,
      });
    }
    this.cellValueChanged.emit(event);
  }

  /**
   * `EditableCellWithUndoGridHost` surface — passed into the renderer via
   * `gridOptions.context.ssrmGridHost`. Methods are arrow-style fields so
   * the `this` binding survives the context handoff without manual binding
   * at every call site. Returns synchronously — no async paths in any of
   * these helpers.
   */
  protected readonly editableCellHost: EditableCellWithUndoGridHost = {
    isCellModified: (rowId, field) =>
      this.modifiedCellsByRow.get(rowId)?.has(field) ?? false,
    isCellSaving: (rowId, field) =>
      this.currentSavingCells.get(rowId)?.has(field) ?? false,
    onCellUndoClicked: (rowId, field) =>
      this.handleCellUndoClicked(rowId, field),
  };

  /**
   * Undo a single cell's pending edit. Sequence (order matters):
   *
   *   1. Read `originalValue` out of `modifiedCellsByRow` for (rowId, field).
   *      Bail if there's no entry — defensive guard for a stale renderer
   *      click after the row evicted or the edit was cleared elsewhere.
   *   2. Delete the entry from `modifiedCellsByRow` (and prune the row's
   *      inner Map if it just emptied). Done BEFORE the setDataValue call
   *      below so even if AG-Grid synchronously re-enters this codepath
   *      (it doesn't, but defense-in-depth) we don't double-process.
   *   3. Revert `node.data` via `setDataValue(field, originalValue,
   *      CDW_UNDO_EVENT_SOURCE)`. The sentinel `source` string causes our
   *      own `onCellValueChanged` to short-circuit so the field isn't
   *      re-added to `modifiedCellsByRow` and the consumer's handler is
   *      not invoked — they're notified through `(cellUndoClicked)`
   *      instead, which carries the explicit undo semantics.
   *   4. Force a cell re-render so the `.modified-cell` class drops AND
   *      the renderer re-evaluates (its `isModified` flips to false and
   *      the undo button hides). `setDataValue` alone doesn't always
   *      re-run cellClass; explicit `refreshCells` is the proven pattern
   *      (see also `applyRowUpdates` and `onCellValueChanged`).
   *   5. Emit `(cellUndoClicked)` for the consumer to drop the matching
   *      cellChanges entry, recompute editChangeCount, and reconcile any
   *      derived state (idAllowList$ for "Show changes only").
   */
  protected handleCellUndoClicked(rowId: string, field: string): void {
    const fields = this.modifiedCellsByRow.get(rowId);
    if (!fields?.has(field)) return;
    const originalValue = fields.get(field);
    fields.delete(field);
    if (fields.size === 0) {
      this.modifiedCellsByRow.delete(rowId);
    }
    const node = this.gridApi?.getRowNode(rowId);
    if (node) {
      // Revert node.data via setDataValue so AG-Grid's column system
      // (which handles dotted field paths through the column's
      // valueSetter) writes to the right leaf — `vendorProperties.google.notes`
      // and friends need the dotted resolution. The sentinel source
      // shields against the cellValueChanged re-entry path.
      node.setDataValue(field, originalValue, CDW_UNDO_EVENT_SOURCE);
      // setDataValue can leave the cellClass evaluation stale on
      // partial-store SSRM blocks; mirror the post-cellValueChanged
      // refreshCells pattern above for the modified→unmodified
      // transition to fire reliably.
      const column = this.columnApi?.getColumn(field) ?? undefined;
      this.gridApi?.refreshCells({
        rowNodes: [node],
        columns: column ? [column] : undefined,
        force: true,
      });
    }
    this.cellUndoClicked.emit({ rowId, field });
  }

  /**
   * Wrap consumer-supplied colDefs with three independent passes:
   *   1. **Header icon** — any colDef with `headerIcon` gets a
   *      `headerComponent: 'customHeaderComponent'` wired in so the icon
   *      renders next to the displayName via `CustomHeaderComponent`.
   *   2. **Editable cells** — any colDef with truthy `editable` gets
   *      `editable` and `cellClass` replaced with callbacks that close
   *      over `modifiedCellsByRow` and `currentSavingCells`. The
   *      `editable` wrapper rejects edits on cells whose prior edit is
   *      still in-flight (race-condition guard); the `cellClass` wrapper
   *      applies `.saving-cell` (priority) or `.modified-cell`.
   *   3. **Editable-cell undo overlay** — editable columns also get
   *      their `cellRenderer` replaced with `editableCellWithUndo` so
   *      cells with a pending edit render a per-cell undo button that
   *      reverts JUST that cell (in contrast with the Cancel button on
   *      the consumer's edit bar which discards ALL pending edits). The
   *      renderer falls back to AG-Grid's default text rendering — see
   *      `EditableCellWithUndoComponent.formatDisplayValue`. If a future
   *      consumer needs a CUSTOM cellRenderer on an editable column, the
   *      wrapper currently overwrites it (MVP limit) — composition would
   *      require a renderer-of-renderers and was deferred per the
   *      EDUS-cell-undo grill outcome.
   *
   * `originalEditables[colId]` preserves the consumer's per-cell editable
   * function (e.g., permission checks) so the wrapper delegates rather
   * than overwrites.
   */
  private wrapColumnDefs(colDefs: CdwColDef[]): CdwColDef[] {
    this.originalEditables.clear();
    return colDefs.map((def) => {
      const colId = def.colId ?? def.field;
      let updated = def;
      if (def.headerIcon) {
        updated = {
          ...updated,
          headerComponent: 'customHeaderComponent',
          headerComponentParams: {
            icon: def.headerIcon,
            tooltip: def.headerTooltip,
          },
          // The tooltip moves onto the icon (MatTooltip, anchored below it).
          // Leaving `headerTooltip` set would ALSO fire AG-Grid's own
          // mouse-following tooltip for the same copy (EDUS-2346 QA).
          headerTooltip: undefined,
        };
      }
      if (!(colId && def.editable)) return updated;
      this.originalEditables.set(colId, def.editable);
      const originalCellClass = updated.cellClass;
      return {
        ...updated,
        // Override any consumer-supplied cellRenderer on editable columns
        // with our undo-overlay wrapper. Today no editable column in
        // device-search ships a custom cellRenderer, so this is a clean
        // replacement; a future consumer that needs composition would
        // extend the renderer to delegate.
        cellRenderer: 'editableCellWithUndo',
        editable: (params: EditableCallbackParams) => {
          // Block editing on cells whose previous edit is still in-flight.
          // Without this, the user's second edit during the job-started →
          // completion window would be overwritten by `applyRowUpdates`
          // injecting the server's stale view of the in-flight value. The
          // .saving-cell CSS sets `pointer-events: none` to block mouse;
          // this gate covers keyboard nav and programmatic triggers.
          const data = params.data as { id?: string | number } | undefined;
          const field = params.colDef.field;
          if (data?.id != null && field) {
            const rowId = String(data.id);
            if (this.currentSavingCells.get(rowId)?.has(field)) return false;
          }
          const orig = this.originalEditables.get(colId);
          return typeof orig === 'function' ? orig(params) : !!orig;
        },
        cellClass: (params: CellClassParams) => {
          const baseClasses = this.evaluateOriginalCellClass(
            originalCellClass,
            params
          );
          const data = params.data as { id?: string | number } | undefined;
          const field = params.colDef.field;
          if (data?.id == null || !field) return baseClasses;
          const rowId = String(data.id);
          // Saving-cell takes priority over modified-cell. The modified
          // tracking is cleared at exitEditMode time, but the priority
          // ordering keeps the right visual if both ever overlap.
          if (this.currentSavingCells.get(rowId)?.has(field)) {
            return [...baseClasses, 'saving-cell'];
          }
          if (this.modifiedCellsByRow.get(rowId)?.has(field)) {
            return [...baseClasses, 'modified-cell'];
          }
          return baseClasses;
        },
      };
    });
  }

  /**
   * Evaluate a colDef's `cellClass` (which AG-Grid types as
   * `string | string[] | ((params) => string | string[] | undefined) | undefined`)
   * into a string array we can spread our augmentation classes onto.
   * Tolerates undefined and string/array shapes; returns [] when nothing is set.
   */
  private evaluateOriginalCellClass(
    cellClass: CdwColDef['cellClass'],
    params: CellClassParams
  ): string[] {
    if (cellClass == null) return [];
    const value = typeof cellClass === 'function' ? cellClass(params) : cellClass;
    if (value == null) return [];
    return Array.isArray(value) ? [...value] : [value];
  }

  /**
   * Watch the grid host subtree for changes and re-inject column-selector
   * pencil icons whenever AG-Grid rebuilds the tool-panel DOM (sidebar
   * open/close, column reorder, columnDefs replace, theme switch).
   * Scoped to `this.elementRef.nativeElement` so multiple grids on the
   * page don't see each other's mutations.
   *
   * Debounced via `requestAnimationFrame` so a burst of mutations
   * (typical when AG-Grid rebuilds the panel) collapses into a single
   * inject pass.
   */
  private setupColumnSelectorObserver(): void {
    this.columnSelectorObserver?.disconnect();
    this.columnSelectorObserver = new MutationObserver(() => {
      requestAnimationFrame(() => this.injectColumnSelectorIcons());
    });
    const observeTarget = (): void => {
      const agRoot =
        this.elementRef.nativeElement.querySelector('.ag-root-wrapper');
      if (agRoot) {
        this.columnSelectorObserver?.observe(agRoot, {
          childList: true,
          subtree: true,
        });
      } else {
        // Pre-onGridReady the wrapper may not exist yet; retry briefly.
        setTimeout(observeTarget, 100);
      }
    };
    observeTarget();
  }

  /**
   * Inject Material Icon spans into the column tool-panel labels for any
   * column whose `CdwColDef.columnSelectorIcon` is set. AG-Grid v28 has
   * no public API to contribute custom DOM into a column-list row, so
   * we walk the rendered list and append a `<span class="material-icons">`
   * to the matching `.ag-column-select-column-label`.
   *
   * Match key is the column's `headerName` (text content of the label).
   * `columnDefs` with no `headerName` or no `columnSelectorIcon` are
   * skipped. Existing `.cdw-column-selector-icon` spans are removed
   * first so re-injection (after `setColumnDefs` or panel rebuild)
   * doesn't duplicate.
   *
   * `requestAnimationFrame` retry loop tolerates the brief window
   * between AG-Grid signaling tool-panel-visible and finishing the
   * actual DOM render; up to 10 frames (~160ms) before giving up.
   *
   * Scoped to `this.elementRef.nativeElement` (same reasoning as the
   * MutationObserver above).
   */
  private injectColumnSelectorIcons(): void {
    const columnsWithIcons = this.columnDefs.filter(
      (col) => col.columnSelectorIcon && col.headerName
    );
    if (columnsWithIcons.length === 0) return;

    const injectIcons = (): boolean => {
      const sidebar =
        this.elementRef.nativeElement.querySelector('.ag-side-bar');
      if (!sidebar) return false;
      const columnItems = sidebar.querySelectorAll(
        '.ag-column-select-column'
      );
      // Less than 2 means only "Select All" has rendered — wait for the
      // per-column rows to land.
      if (columnItems.length < 2) return false;

      for (const col of columnsWithIcons) {
        let labelElement: HTMLElement | null = null;
        for (const item of Array.from(columnItems)) {
          const labelSpan = item.querySelector(
            '.ag-column-select-column-label'
          );
          if (labelSpan?.textContent?.trim() === col.headerName) {
            labelElement = labelSpan as HTMLElement;
            break;
          }
        }
        if (!labelElement) continue;
        labelElement
          .querySelector('.cdw-column-selector-icon')
          ?.remove();
        const iconSpan = document.createElement('span');
        iconSpan.className = 'material-icons cdw-column-selector-icon';
        iconSpan.textContent = col.columnSelectorIcon ?? '';
        iconSpan.style.fontSize = '16px';
        iconSpan.style.marginLeft = '4px';
        iconSpan.style.paddingBottom = '2px';
        iconSpan.style.verticalAlign = 'middle';
        iconSpan.style.color = '#666';
        labelElement.appendChild(iconSpan);
      }
      return true;
    };

    const tryInject = (attempt = 1, maxAttempts = 10): void => {
      requestAnimationFrame(() => {
        if (!injectIcons() && attempt < maxAttempts) {
          tryInject(attempt + 1, maxAttempts);
        }
      });
    };
    tryInject();
  }

  private buildGridOptions(): GridOptions {
    return {
      // SSRM-specific config
      rowModelType: 'serverSide',
      serverSideStoreType: 'partial',
      cacheBlockSize: resolveCacheBlockSize(this.currentPageSize, this.cacheBlockSize),
      maxBlocksInCache: this.maxBlocksInCache,

      // Default column behavior. AG-Grid v28's per-column defaults are
      // `resizable: false` and `sortable: false`; without this block the
      // user can't drag column boundaries (no resize handles render at
      // header cell edges) and headers don't sort on click. Per-column
      // `resizable: false` / `sortable: false` overrides still win
      // (e.g., the View column intentionally suppresses both).
      defaultColDef: {
        resizable: true,
        sortable: true,
      },

      // Standard grid config (mirrors CdwSearchGridComponent for behavioral parity)
      pagination: true,
      paginationPageSize: this.currentPageSize,
      suppressLoadingOverlay: true,
      animateRows: true,
      rowSelection: 'multiple',
      suppressRowClickSelection: true,
      // Selection-lock visual + functional gate is CSS-only — see
      // component.scss `.cdw-ssrm-grid--selection-locked` block for the
      // full v27→v28 migration rationale. Defensive gating for keyboard
      // / programmatic paths lives in `onSelectionChanged`.
      // Force AG-Grid v28 to dispatch gridOptions.on* callbacks
      // SYNCHRONOUSLY rather than via the default `setTimeout`-batched
      // `dispatchAsync` path. Three commits worth of "Save click doesn't
      // dispatch" bugs traced to this: callers like `onSaveChanges` →
      // `commitOpenEditor` → `gridApi.stopEditing(false)` rely on the
      // consumer's `onCellValueChanged` having already populated
      // `cellChanges` before the next line of code runs. With async
      // dispatch (the v28 default), the callback runs in a future
      // macrotask, so the size check fires against stale state and
      // returns early. `toggleShowChangesOnly` had the same latent
      // off-by-one-tick bug. Sync dispatch makes
      // commit→populate→check→dispatch atomic. Per project decision
      // "correctness > speed".
      suppressAsyncEvents: true,
      // Opt-in single-click-to-edit. Defaults to AG-Grid's double-click
      // behavior when this @Input is unset.
      singleClickEdit: this.singleClickEdit,
      overlayNoRowsTemplate: `<span>${this.noRowsOverlayText}</span>`,
      enableRangeSelection: true,
      processCellForClipboard: (params) => params?.value ?? '',

      // SSRM requires getRowId so AG-Grid can match incoming block data
      // against existing row nodes during refreshServerSide / transactions.
      getRowId: (params: GetRowIdParams<T>) => String(params.data.id),

      // Visual treatment: rows that are stale (server-pushed via asset
      // cache `meta`) or updating (augmented from the external set, Phase
      // 5a) get the `stale-row` class. Same logic as the legacy grid
      // (`cdw-search-grid.component.ts:2042`) so styles in
      // `libs/common-frontend-materials/styles.scss` carry over verbatim.
      getRowClass: (params: RowClassParams<T>) => {
        const data = params.data as
          | { isStale?: boolean; isUpdating?: boolean }
          | undefined;
        return data?.isStale || data?.isUpdating ? 'stale-row' : undefined;
      },

      // Register the existing PreviewIconComponent so consumers can include
      // the `previewColumn` constant (or any column with `cellRenderer:
      // 'previewIconComponent'`) in their columnDefs and have the eye /
      // clock-grayed / spinner glyph render based on `data.isStale` and
      // `data.isUpdating`. The component is registered, but auto-insertion
      // of the column itself is left to consumers — they decide whether
      // the preview column is wanted (the grid is generic; not all
      // consumers want a preview affordance).
      components: {
        previewIconComponent: PreviewIconComponent<T>,
        // SSRM-aware header checkbox for "select all on page".
        // AG-Grid 28's built-in `headerCheckboxSelection: true` doesn't
        // render under SSRM (the header cell stays empty); this custom
        // header fills that gap. Consumer references it via
        // `headerComponent: 'ssrmHeaderCheckbox'` on the checkbox column.
        ssrmHeaderCheckbox: SsrmHeaderCheckboxComponent,
        // Header component that renders displayName + an optional Material
        // icon. Auto-applied by `wrapColumnDefs` to any colDef with the
        // custom `headerIcon` property set (e.g., `headerIcon: 'edit'`).
        customHeaderComponent: CustomHeaderComponent,
        // Per-cell undo overlay renderer. Auto-applied by `wrapColumnDefs`
        // to any colDef with truthy `editable`; the renderer reads
        // modified/saving state via `params.context.ssrmGridHost` (set
        // below) and on click calls `handleCellUndoClicked` on the grid
        // component to revert just that cell.
        editableCellWithUndo: EditableCellWithUndoComponent,
      },

      // `gridOptions.context` is AG-Grid's catch-all for per-grid arbitrary
      // data accessible from cell renderers via `params.context`. Used here
      // to hand the renderer a `ssrmGridHost` reference so it can ask the
      // grid component whether a cell is currently modified / saving and
      // forward undo-click events back. The renderer treats this as an
      // opaque host interface (`EditableCellWithUndoGridHost`) so the
      // renderer file doesn't need to import the grid component class
      // (which would create a circular module reference).
      context: { ssrmGridHost: this.editableCellHost },

      onGridReady: (event: GridReadyEvent<T>) => this.onGridReady(event),
      onRowClicked: (event: RowClickedEvent) => this.onRowClicked(event),
      onCellClicked: (event: CellClickedEvent) => this.onCellClicked(event),
      onFirstDataRendered: () => this.onFirstDataRendered(),
      onModelUpdated: () => this.onModelUpdated(),
      onColumnVisible: () => this.onColumnVisible(),
      onColumnMoved: () => this.onColumnMoved(),
      onColumnResized: (event: ColumnResizedEvent) =>
        this.onColumnResized(event),
      onSortChanged: () => this.onSortChanged(),
      onNewColumnsLoaded: () => this.onNewColumnsLoaded(),
      onSelectionChanged: () => this.onSelectionChanged(),
      onPaginationChanged: () => this.onPaginationChanged(),
      // In-cell edit events. cellValueChanged is routed through our
      // handler so the grid can track modified-cell state internally
      // AND re-emit for the consumer; the editing-started/stopped pair
      // is bubbled up so consumers can gate Cancel/Save UI on editor
      // open (rather than waiting for a value-commit).
      onCellValueChanged: (event: CellValueChangedEvent) =>
        this.onCellValueChanged(event),
      onCellEditingStarted: (event: CellEditingStartedEvent) =>
        this.handleCellEditingStarted(event),
      onCellEditingStopped: (event: CellEditingStoppedEvent) =>
        this.handleCellEditingStopped(event),
    };
  }
}
